import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { BrandLogo } from '@/components/BrandLogo';
import DriverDashboardHome from '@/components/DriverDashboardHome';
import { buildContractPdfFilename } from '@/utils/contractDownload';
import {
  Truck,
  LogOut,
  User,
  FileText,
  Package,
  Upload,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Home,
  Image,
  Edit,
  Search,
  Filter,
  X,
  DollarSign,
  Calendar,
  TrendingUp,
  Receipt,
  Eye,
  Menu,
  Download,
  Languages,
  Loader2
} from 'lucide-react';

function formatEuro(value, digits = 2) {
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(digits) : (0).toFixed(digits);
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to get emoji for additional services
const getServiceEmoji = (serviceName) => {
  if (!serviceName) return '➕';
  
  const lowerName = serviceName.toLowerCase();
  
  // Limpieza
  if (lowerName.includes('limpieza')) return '🧽✨';
  
  // ITV
  if (lowerName.includes('itv') || lowerName.includes('inspección técnica')) return '🔍✅';
  
  // PIT STOP
  if (lowerName.includes('pit stop') || lowerName.includes('pit-stop') || lowerName.includes('mantenimiento')) return '🛠️';
  
  // Recuperación
  if (lowerName.includes('recuperación') || lowerName.includes('recuperacion')) {
    if (lowerName.includes('europa') || lowerName.includes('continental')) return '🛡️🗺️';
    if (lowerName.includes('isla') || lowerName.includes('islas') || lowerName.includes('canarias') || lowerName.includes('baleares')) return '🛡️🏝️';
    if (lowerName.includes('ceuta') || lowerName.includes('melilla') || lowerName.includes('áfrica') || lowerName.includes('africa')) return '🛡️🌍';
    return '🛡️';
  }
  
  // Representación
  if (lowerName.includes('representación') || lowerName.includes('representacion') || lowerName.includes('verificación') || lowerName.includes('verificacion')) return '🔍📋';
  
  // Default
  return '➕';
};

// Helper function to check if delivery date has passed
const isDeliveryDatePassed = (order) => {
  if (!order) return false;
  
  const now = new Date();
  
  // Try delivery_date_from first
  if (order.delivery_date_from) {
    const deliveryDate = new Date(order.delivery_date_from);
    // If there's a delivery time, use it
    if (order.delivery_time_end) {
      const [hours, minutes] = order.delivery_time_end.split(':').map(Number);
      deliveryDate.setHours(hours, minutes, 0, 0);
    }
    return now > deliveryDate;
  }
  
  // Fallback to service_date
  if (order.service_date) {
    const serviceDate = new Date(order.service_date);
    serviceDate.setHours(23, 59, 59, 999); // End of day
    return now > serviceDate;
  }
  
  return false;
};

// Function to compress image before upload
const compressImage = (file, maxWidth = 800, quality = 0.6, maxHeight = 800) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new window.Image();
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          // Calculate new dimensions - more aggressive compression
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          
          // Also limit height
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to base64 with compression
          const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
          resolve(compressedBase64);
        } catch (err) {
          reject(err);
        }
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target.result;
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};

// Expense receipts are small printed text — the 800px photo profile makes them unreadable
// for admins approving expenses. Keep the longest edge at 2000px (~400–600KB per ticket).
const RECEIPT_COMPRESSION = { maxWidth: 2000, maxHeight: 2000, quality: 0.82 };

// Function to process multiple files with compression
const processFilesWithCompression = async (files, profile = null) => {
  const compressedPhotos = [];
  for (const file of files) {
    try {
      const compressed = profile
        ? await compressImage(file, profile.maxWidth, profile.quality, profile.maxHeight)
        : await compressImage(file);
      compressedPhotos.push(compressed);
    } catch (error) {
      console.error('Error compressing image:', error);
      // Fallback to original if compression fails
      const reader = new FileReader();
      const base64 = await new Promise((resolve) => {
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });
      compressedPhotos.push(base64);
    }
  }
  return compressedPhotos;
};

const DriverDashboard = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language?.startsWith('es') ? 'es-ES' : 'en-US';
  const toggleLanguage = () => {
    const nextLang = (i18n.language || '').startsWith('es') ? 'en' : 'es';
    i18n.changeLanguage(nextLang);
  };
  const [driver, setDriver] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [orders, setOrders] = useState([]);
  const [activeTab, setActiveTab] = useState('home');
  const [servicesSubTab, setServicesSubTab] = useState('active'); // Sub-tabs: active or historical
  const [loading, setLoading] = useState(true);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showCreateMemberDialog, setShowCreateMemberDialog] = useState(false);
  const [showEditMemberDialog, setShowEditMemberDialog] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false); // Mobile menu state
  const [expandedOrderId, setExpandedOrderId] = useState(null); // For compact list expand/collapse
  
  // Service filters
  const [serviceFilters, setServiceFilters] = useState({
    searchText: '',
    dateFrom: '',
    dateTo: '',
    status: 'all', // all, assigned, in_progress, completed
    paymentStatus: 'all', // all, paid, unpaid
    vehicleBrand: '',
    vehicleModel: '',
    vehiclePlate: '',
    fuelType: 'all', // all, gasolina, diesel, electrico, hibrido
    driver: 'all' // all, or specific driver_id
  });
  
  // Billing state
  const [billingPeriods, setBillingPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [periodDetails, setPeriodDetails] = useState(null);
  const [loadingBilling, setLoadingBilling] = useState(false);
  const [showPeriodDialog, setShowPeriodDialog] = useState(false);
  const [expandedReceipts, setExpandedReceipts] = useState({}); // Track which expense reports are expanded
  const [currentPeriodData, setCurrentPeriodData] = useState(null); // Current period "EN CURSO"
  const [loadingCurrentPeriod, setLoadingCurrentPeriod] = useState(false);
  const [showCurrentPeriodDetails, setShowCurrentPeriodDetails] = useState(false);
  const [driverInvoices, setDriverInvoices] = useState([]);
  const [loadingDriverInvoices, setLoadingDriverInvoices] = useState(false);
  
  // Fleet management state
  const [fleetMembers, setFleetMembers] = useState([]);
  const [loadingFleet, setLoadingFleet] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedOrderForAssignment, setSelectedOrderForAssignment] = useState(null);
  
  // Incomplete orders state
  const [incompleteOrders, setIncompleteOrders] = useState([]);
  const [loadingIncomplete, setLoadingIncomplete] = useState(false);
  const [showCompleteServiceDialog, setShowCompleteServiceDialog] = useState(false);
  const [selectedOrderToComplete, setSelectedOrderToComplete] = useState(null);
  const [driverPricingTemplates, setDriverPricingTemplates] = useState([]); // For displaying pricing template names
  
  // Simplified photo states (multiple photos per section)
  const [pickupPhotos, setPickupPhotos] = useState([]);  // Array of base64 photos
  const [deliveryPhotos, setDeliveryPhotos] = useState([]);  // Array of base64 photos
  const [contractPhotos, setContractPhotos] = useState([]);  // Array of base64 photos
  const [receiptPhotos, setReceiptPhotos] = useState([]);  // Array of base64 photos for expenses
  
  const [uploadingPickupPhotos, setUploadingPickupPhotos] = useState(false);
  const [uploadingDeliveryPhotos, setUploadingDeliveryPhotos] = useState(false);
  const [uploadingContractPhotos, setUploadingContractPhotos] = useState(false);
  const contractPdfDownloadLockRef = useRef(false);
  const [downloadingContractOrderId, setDownloadingContractOrderId] = useState(null);
  
  // Expense report state
  const [expenseForm, setExpenseForm] = useState({
    fuel_expense: 0,
    toll_expense: 0,
    parking_expense: 0,
    taxi_expense: 0,
    other_expense: 0,
    other_expense_description: '',
    receipts: [] // URLs of uploaded receipt photos
  });
  const [uploadingExpenses, setUploadingExpenses] = useState(false);
  
  // Profile editing states
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    full_name: '',
    dni_nie: '',
    fiscal_address: '',
    irpf_retention: 0,
    bank_name: '',
    bank_account: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);
  
  // Incident reporting states
  const [showIncidentDialog, setShowIncidentDialog] = useState(false);
  const [selectedOrderForIncident, setSelectedOrderForIncident] = useState(null);
  const [incidentForm, setIncidentForm] = useState({
    incident_type: 'pickup_unavailable',
    description: '',
    cancellation_reason: '',
    contract_photo: null,
    vehicle_photo: null
  });
  const [submittingIncident, setSubmittingIncident] = useState(false);
  
  const [uploadForm, setUploadForm] = useState({
    document_type: '',
    file: null,
    upload_date: new Date().toISOString().split('T')[0],
    expiry_date: ''
  });
  const [createMemberForm, setCreateMemberForm] = useState({
    name: '',
    email: '',
    phone: '',
    license_number: '',
    dni_nie: ''
  });
  const [memberDocuments, setMemberDocuments] = useState({
    license_front: null,
    license_back: null,
    dni_front: null,
    dni_back: null
  });

  useEffect(() => {
    const token = localStorage.getItem('driver_token');
    const driverData = localStorage.getItem('driver_data');
    
    if (!token || !driverData) {
      navigate('/driver/login');
      return;
    }
    
    setDriver(JSON.parse(driverData));
    loadData();
    
    // Load fleet members if this is a fleet driver
    const driver = JSON.parse(driverData);
    if (driver.is_fleet_driver) {
      loadFleetMembers();
    }
  }, [navigate]);

  // Load fleet members when Mi Flota tab is selected
  useEffect(() => {
    if (activeTab === 'fleet' && driver?.is_fleet_driver) {
      loadFleetMembers();
    }
  }, [activeTab, driver?.is_fleet_driver]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('driver_token');
      const driverData = JSON.parse(localStorage.getItem('driver_data') || '{}') || {};

      // Load documents
      try {
        const docsResponse = await fetch(`${BACKEND_URL}/api/drivers/me/documents`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (docsResponse.ok) {
          const docsData = await docsResponse.json();
          setDocuments(Array.isArray(docsData) ? docsData : []);
        }
      } catch (err) {
        console.error('Error loading documents:', err);
        // Continue loading other data
      }
      
      // Load assigned orders - use driver-specific endpoint
      try {
        const ordersResponse = await fetch(`${BACKEND_URL}/api/drivers/orders`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (ordersResponse.ok) {
          const ordersData = await ordersResponse.json();
          setOrders(Array.isArray(ordersData) ? ordersData : []);
        }
      } catch (err) {
        console.error('Error loading orders:', err);
        // Orders endpoint may not be available for drivers yet
        setOrders([]);
      }
      
      // Load fleet members if this is a fleet driver
      if (driverData.is_fleet_driver) {
        try {
          const fleetResponse = await fetch(`${BACKEND_URL}/api/drivers/fleet/members`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (fleetResponse.ok) {
            const fleetData = await fleetResponse.json();
            setFleetMembers(Array.isArray(fleetData) ? fleetData : []);
          }
        } catch (err) {
          console.error('Error loading fleet members:', err);
          setFleetMembers([]);
        }
      }
      
      // Load driver pricing templates
      try {
        const templatesResponse = await fetch(`${BACKEND_URL}/api/driver-pricing-templates`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (templatesResponse.ok) {
          const templatesData = await templatesResponse.json();
          setDriverPricingTemplates(Array.isArray(templatesData) ? templatesData : []);
        }
      } catch (err) {
        console.error('Error loading pricing templates:', err);
        setDriverPricingTemplates([]);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      // Only show error if it's a critical failure
      setLoading(false);
    }
  };

  // Function to download contract PDF using form method
  const handleDownloadContract = async (orderId) => {
    if (!orderId || contractPdfDownloadLockRef.current) return;
    contractPdfDownloadLockRef.current = true;
    setDownloadingContractOrderId(orderId);
    try {
      const token = localStorage.getItem('driver_token');
      const response = await fetch(
        `${BACKEND_URL}/api/drivers/orders/${orderId}/contract?_=${Date.now()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          cache: 'no-store',
        }
      );
      
      if (response.ok) {
        const renderer = response.headers.get('X-AutoGo-Contract-Renderer');
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = buildContractPdfFilename(orderId);
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success(t('driver_toast_contract_downloaded'));
        if (renderer === 'reportlab') {
          toast.warning(t('service_toast_contract_fallback_simple_pdf'));
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || t('driver_toast_error_download_contract'));
      }
    } catch (error) {
      console.error('Error downloading contract:', error);
      toast.error(t('driver_toast_error_download_contract'));
    } finally {
      contractPdfDownloadLockRef.current = false;
      setDownloadingContractOrderId(null);
    }
  };

  const handleStartService = async (serviceId) => {
    try {
      const token = localStorage.getItem('driver_token');
      const response = await fetch(`${BACKEND_URL}/api/drivers/transport-services/${serviceId}/start`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      let data = {};
      try {
        data = await response.json();
      } catch {
        data = {};
      }
      if (response.ok) {
        toast.success(t('driver_toast_service_started'));
        await loadData();
      } else {
        toast.error(data.detail || t('service_toast_error_update_service'));
      }
    } catch (error) {
      console.error('Error starting service:', error);
      toast.error(t('service_toast_error_update_service'));
    }
  };

  // Fleet management functions
  const loadFleetMembers = async () => {
    if (!driver?.is_fleet_driver) return;
    
    setLoadingFleet(true);
    try {
      const token = localStorage.getItem('driver_token');
      const response = await fetch(`${BACKEND_URL}/api/drivers/fleet/members`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFleetMembers(data);
      }
    } catch (error) {
      console.error('Error loading fleet members:', error);
      toast.error(t('driver_toast_error_load_drivers'));
    } finally {
      setLoadingFleet(false);
    }
  };

  const handleCreateFleetMember = async (e) => {
    e.preventDefault();
    setLoadingFleet(true);
    
    try {
      const token = localStorage.getItem('driver_token');
      
      
      const response = await fetch(`${BACKEND_URL}/api/drivers/fleet/members`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: createMemberForm.name,
          email: createMemberForm.email,
          phone: createMemberForm.phone,
          license_number: createMemberForm.license_number,
          dni_nie: createMemberForm.dni_nie
        })
      });
      
      
      // Read the response body once and store it
      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        console.error('Failed to parse JSON response:', jsonError);
        throw new Error(t('driver_toast_invalid_server_response'));
      }
      
      if (response.ok) {
        // Show appropriate message based on whether password is included (email failed)
        if (data.temp_password) {
          toast.success(
            t('driver_toast_driver_created_with_password', { temp_password: data.temp_password }),
            { duration: 10000 }
          );
        } else {
          toast.success(data.message || t('driver_toast_driver_created'));
        }
        setShowCreateMemberDialog(false);
        setCreateMemberForm({
          name: '',
          email: '',
          phone: '',
          license_number: '',
          dni_nie: ''
        });
        await loadFleetMembers();
      } else {
        console.error('Error response:', data);
        // Handle different error types
        const errorMessage = data.detail || data.message || t('driver_error_create_driver_fallback');
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Exception creating fleet member:', error);
      toast.error(t('driver_toast_error_create_driver', { message: error.message }));
    } finally {
      setLoadingFleet(false);
    }
  };

  const handleUpdateFleetMember = async (memberId, updates) => {
    setLoadingFleet(true);
    
    try {
      const token = localStorage.getItem('driver_token');
      const response = await fetch(`${BACKEND_URL}/api/drivers/fleet/members/${memberId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(t('driver_toast_driver_updated'));
        setShowEditMemberDialog(false);
        setEditingMember(null);
        await loadFleetMembers();
      } else {
        toast.error(data.detail || t('driver_toast_error_update_driver'));
      }
    } catch (error) {
      console.error('Error updating fleet member:', error);
      toast.error(t('driver_toast_error_update_driver'));
    } finally {
      setLoadingFleet(false);
    }
  };

  const handleDeleteFleetMember = async (memberId, memberName) => {
    if (!window.confirm(t('driver_confirm_delete_member', { name: memberName }))) return;
    
    setLoadingFleet(true);
    
    try {
      const token = localStorage.getItem('driver_token');
      const response = await fetch(`${BACKEND_URL}/api/drivers/fleet/members/${memberId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(t('driver_toast_driver_deleted'));
        await loadFleetMembers();
      } else {
        toast.error(data.detail || t('driver_toast_error_delete_driver'));
      }
    } catch (error) {
      console.error('Error deleting fleet member:', error);
      toast.error(t('driver_toast_error_delete_driver'));
    } finally {
      setLoadingFleet(false);
    }
  };

  const handleAssignToFleetMember = async (memberId) => {
    if (!selectedOrderForAssignment) return;
    
    setLoadingFleet(true);
    
    try {
      const token = localStorage.getItem('driver_token');
      const response = await fetch(`${BACKEND_URL}/api/drivers/fleet/assign-service?order_id=${selectedOrderForAssignment.id}&member_id=${memberId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        const isReassignment = selectedOrderForAssignment.driver_id && selectedOrderForAssignment.driver_id !== memberId;
        const isSelf = memberId === driver.id;
        
        if (isSelf) {
          toast.success(t('driver_toast_service_assigned_self'));
        } else if (isReassignment) {
          toast.success(t('driver_toast_service_reassigned'));
        } else {
          toast.success(t('driver_toast_service_assigned'));
        }
        
        setShowAssignDialog(false);
        setSelectedOrderForAssignment(null);
        await loadData(); // Reload orders
      } else {
        toast.error(data.detail || t('driver_toast_error_assign_service'));
      }
    } catch (error) {
      console.error('Error assigning service:', error);
      toast.error(t('driver_toast_error_assign_service'));
    } finally {
      setLoadingFleet(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('driver_token');
    localStorage.removeItem('driver_data');
    toast.success(t('driver_toast_logged_out'));
    navigate('/driver/login');
  };

  // Incident reporting function
  const handleSubmitIncident = async () => {
    if (!selectedOrderForIncident) return;
    
    setSubmittingIncident(true);
    try {
      const token = localStorage.getItem('driver_token');
      const response = await fetch(`${BACKEND_URL}/api/drivers/orders/${selectedOrderForIncident.id}/incidents`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          incident_type: incidentForm.incident_type,
          title: incidentForm.incident_type === 'pickup_unavailable' ? t('admin_incident_type_pickup_unavailable') : '',
          description: incidentForm.description,
          cancellation_reason: incidentForm.cancellation_reason,
          contract_photo: incidentForm.contract_photo,
          vehicle_photo: incidentForm.vehicle_photo,
          photos: []
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(t('driver_toast_incident_reported'));
        setShowIncidentDialog(false);
        setSelectedOrderForIncident(null);
        setIncidentForm({
          incident_type: 'pickup_unavailable',
          description: '',
          cancellation_reason: '',
          contract_photo: null,
          vehicle_photo: null
        });
        await loadData(); // Reload to update order status
      } else {
        toast.error(data.detail || t('driver_toast_error_report_incident'));
      }
    } catch (error) {
      console.error('Error reporting incident:', error);
      toast.error(t('driver_toast_error_report_incident'));
    } finally {
      setSubmittingIncident(false);
    }
  };

  const loadIncompleteOrders = async () => {
    setLoadingIncomplete(true);
    try {
      const token = localStorage.getItem('driver_token');
      const response = await fetch(`${BACKEND_URL}/api/drivers/incomplete-orders`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setIncompleteOrders(data || []);
      }
    } catch (error) {
      console.error('Error loading incomplete orders:', error);
    } finally {
      setLoadingIncomplete(false);
    }
  };

  /** Compress + upload immediately so backend AI analysis starts without a second button click. */
  const uploadOrderSimplePhotos = async (orderId, photoType, photos) => {
    if (!orderId || !photos?.length) return false;
    const token = localStorage.getItem('driver_token');
    const response = await fetch(`${BACKEND_URL}/api/orders/${orderId}/simple-photos`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ photo_type: photoType, photos }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || t('driver_toast_error_upload_photos'));
    }
    return true;
  };

  // Billing functions
  const loadBillingPeriods = async () => {
    try {
      setLoadingBilling(true);
      const token = localStorage.getItem('driver_token');
      
      // Load liquidation periods from new endpoint
      const response = await fetch(`${BACKEND_URL}/api/drivers/liquidations`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setBillingPeriods(data.periods || []);
        // Store fleet info if available
        if (data.is_fleet_owner) {
        }
      }
    } catch (error) {
      console.error('Error loading billing periods:', error);
      toast.error(t('driver_toast_error_load_billing_periods'));
    } finally {
      setLoadingBilling(false);
    }
  };

  const loadDriverInvoices = async () => {
    try {
      setLoadingDriverInvoices(true);
      const token = localStorage.getItem('driver_token');
      const response = await fetch(`${BACKEND_URL}/api/drivers/invoices`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDriverInvoices(Array.isArray(data) ? data : []);
      } else {
        setDriverInvoices([]);
      }
    } catch (error) {
      console.error('Error loading driver invoices:', error);
      setDriverInvoices([]);
    } finally {
      setLoadingDriverInvoices(false);
    }
  };

  // Load current period data (EN CURSO)
  const loadCurrentPeriod = async () => {
    try {
      setLoadingCurrentPeriod(true);
      const token = localStorage.getItem('driver_token');
      
      const response = await fetch(`${BACKEND_URL}/api/drivers/liquidations/current-period`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCurrentPeriodData(data);
      }
    } catch (error) {
      console.error('Error loading current period:', error);
    } finally {
      setLoadingCurrentPeriod(false);
    }
  };

  const viewPeriodDetails = async (period) => {
    try {
      setSelectedPeriod(period);
      setLoadingBilling(true);
      const token = localStorage.getItem('driver_token');
      
      const response = await fetch(`${BACKEND_URL}/api/drivers/liquidations/${period.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPeriodDetails(data);
        setShowPeriodDialog(true);
      } else {
        toast.error(t('driver_toast_error_load_period_details'));
      }
    } catch (error) {
      console.error('Error loading period details:', error);
      toast.error(t('driver_toast_error_load_period_details'));
    } finally {
      setLoadingBilling(false);
    }
  };

  const downloadLiquidationExcel = async (periodId) => {
    try {
      const token = localStorage.getItem('driver_token');
      const response = await fetch(`${BACKEND_URL}/api/drivers/liquidations/${periodId}/excel`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const cd = response.headers.get('Content-Disposition');
        let name = `Liquidacion_${periodId}.xlsx`;
        if (cd) {
          const quoted = cd.match(/filename="([^"]+)"/i);
          const utf = cd.match(/filename\*=UTF-8''([^;\n]+)/i);
          if (utf) {
            try {
              name = decodeURIComponent(utf[1].trim());
            } catch (_) {
              /* keep default */
            }
          } else if (quoted) {
            name = quoted[1];
          }
        }
        a.download = name;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success(t('admin_toast_excel_downloaded'));
      } else {
        toast.error(t('admin_toast_error_download_excel'));
      }
    } catch (error) {
      console.error('Error downloading liquidation excel:', error);
      toast.error(t('admin_toast_error_download_excel'));
    }
  };

  useEffect(() => {
    if (activeTab === 'invoices') {
      if (billingPeriods.length === 0) {
        loadBillingPeriods();
      }
      if (!currentPeriodData) {
        loadCurrentPeriod();
      }
      loadDriverInvoices();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'incomplete') {
      loadIncompleteOrders();
    }
  }, [activeTab]);

  const startEditingProfile = () => {
    // Initialize form with current driver data
    setProfileForm({
      full_name: driver?.full_name || '',
      dni_nie: driver?.dni_nie || '',
      fiscal_address: driver?.fiscal_address || '',
      irpf_retention: driver?.irpf_retention || 0,
      bank_name: driver?.bank_name || '',
      bank_account: driver?.bank_account || ''
    });
    setIsEditingProfile(true);
  };

  const saveProfileChanges = async () => {
    try {
      // Validate required fields
      if (!profileForm.full_name || !profileForm.dni_nie || !profileForm.fiscal_address || 
          !profileForm.bank_name || !profileForm.bank_account) {
        toast.error(t('driver_toast_complete_required_fields'));
        return;
      }

      setSavingProfile(true);
      const token = localStorage.getItem('driver_token');
      
      const response = await fetch(`${BACKEND_URL}/api/drivers/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(profileForm)
      });

      const data = await response.json();

      if (response.ok) {
        setDriver(data);
        localStorage.setItem('driver_data', JSON.stringify(data));
        toast.success(t('driver_toast_profile_updated'));
        setIsEditingProfile(false);
        
        // Show info about bank account change
        if (profileForm.bank_account !== driver?.bank_account) {
          toast.info(t('driver_toast_new_iban_next_invoice'));
        }
      } else {
        toast.error(data.detail || t('driver_toast_error_update_profile'));
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error(t('driver_toast_error_save_changes'));
    } finally {
      setSavingProfile(false);
    }
  };

  const handleEditMember = (member) => {
    setEditingMember({
      ...member,
      documents: {
        license_front: null,
        license_back: null,
        dni_front: null,
        dni_back: null
      }
    });
    setShowEditMemberDialog(true);
  };

  const handleUpdateMember = async (e) => {
    e.preventDefault();
    
    if (!editingMember) return;
    
    setLoadingFleet(true);
    
    try {
      const token = localStorage.getItem('driver_token');
      
      // Update member info using the correct fleet endpoint
      const response = await fetch(`${BACKEND_URL}/api/drivers/fleet/members/${editingMember.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: editingMember.name,
          email: editingMember.email,
          phone: editingMember.phone,
          license_number: editingMember.license_number,
          is_available: editingMember.is_available
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || t('driver_toast_error_update_member'));
      }
      
      // Upload new documents if provided
      let uploadedCount = 0;
      const docTypesToUpload = ['license_front', 'license_back', 'dni_front', 'dni_back'];
      
      for (const docType of docTypesToUpload) {
        if (editingMember.documents && editingMember.documents[docType]) {
          const formData = new FormData();
          formData.append('file', editingMember.documents[docType]);
          formData.append('document_type', docType);
          formData.append('upload_date', new Date().toISOString());
          formData.append('driver_id', editingMember.id);
          
          const docResponse = await fetch(`${BACKEND_URL}/api/drivers/documents/upload`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: formData
          });
          
          if (docResponse.ok) {
            uploadedCount++;
          }
        }
      }
      
      toast.success(
        uploadedCount > 0
          ? t('driver_toast_member_updated_with_docs', { count: uploadedCount })
          : t('driver_toast_member_updated')
      );
      setShowEditMemberDialog(false);
      setEditingMember(null);
      await loadFleetMembers(); // Reload fleet members instead of all data
    } catch (error) {
      console.error('Error updating fleet member:', error);
      toast.error(error.message || t('driver_toast_error_update_member'));
    } finally {
      setLoadingFleet(false);
    }
  };

  const handleDocumentUpload = async (e) => {
    e.preventDefault();
    
    if (!uploadForm.file) {
      toast.error(t('driver_toast_select_file'));
      return;
    }
    
    setUploadingDoc(true);
    
    try {
      const token = localStorage.getItem('driver_token');
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('document_type', uploadForm.document_type);
      formData.append('upload_date', new Date(uploadForm.upload_date).toISOString());
      if (uploadForm.expiry_date) {
        formData.append('expiry_date', new Date(uploadForm.expiry_date).toISOString());
      }
      
      const response = await fetch(`${BACKEND_URL}/api/drivers/documents/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (!response.ok) {
        throw new Error('Failed to upload document');
      }
      
      toast.success(t('driver_toast_document_uploaded'));
      setShowUploadDialog(false);
      setUploadForm({
        document_type: '',
        file: null,
        upload_date: new Date().toISOString().split('T')[0],
        expiry_date: ''
      });
      loadData();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(t('driver_toast_error_upload_document'));
    } finally {
      setUploadingDoc(false);
    }
  };

  const getDocumentStatus = (doc) => {
    const statusConfig = {
      pending: { icon: Clock, color: 'text-yellow-600', bg: 'bg-yellow-100', text: t('driver_doc_pending') },
      approved: { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-100', text: t('driver_doc_approved') },
      rejected: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-100', text: t('driver_doc_rejected') },
      expired: { icon: AlertTriangle, color: 'text-orange-600', bg: 'bg-orange-100', text: t('driver_doc_expired') }
    };
    
    return statusConfig[doc.status] || statusConfig.pending;
  };

  const getDocumentName = (type) => {
    const names = {
      license_front: t('driver_doc_license_front'),
      license_back: t('driver_doc_license_back'),
      dni_front: t('driver_doc_dni_front'),
      dni_back: t('driver_doc_dni_back'),
      autonomo_certificate: t('driver_doc_autonomo'),
      hacienda_certificate: t('driver_doc_hacienda'),
      ss_certificate: t('driver_doc_ss')
    };
    return names[type] || type;
  };

  const requiredDocuments = [
    'license_front',
    'license_back',
    'dni_front',
    'dni_back',
    'autonomo_certificate',
    'hacienda_certificate',
    'ss_certificate'
  ];

  const getDocumentProgress = () => {
    const uploaded = requiredDocuments.filter(docType =>
      documents.some(d => d.document_type === docType)
    ).length;
    return { uploaded, total: requiredDocuments.length };
  };

  // Component to display member documents
  const MemberDocumentsSection = ({ memberId }) => {
    const [memberDocs, setMemberDocs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      const fetchMemberDocuments = async () => {
        try {
          const response = await fetch(`${BACKEND_URL}/api/drivers/me/documents?member_id=${encodeURIComponent(memberId)}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('driver_token')}`
            }
          });

          if (response.ok) {
            const data = await response.json();
            setMemberDocs(Array.isArray(data) ? data : []);
          }
        } catch (error) {
          console.error('Error loading member documents:', error);
        } finally {
          setLoading(false);
        }
      };

      fetchMemberDocuments();
    }, [memberId]);

    if (loading) {
      return <p className="text-sm text-gray-500">{t('driver_docs_loading')}</p>;
    }

    const memberRequiredDocs = ['dni_front', 'dni_back', 'license_front', 'license_back'];
    
    return (
      <div className="space-y-2 mt-3">
        {memberRequiredDocs.map((docType) => {
          const doc = memberDocs.find(d => d.document_type === docType);
          const status = doc ? getDocumentStatus(doc) : null;
          const StatusIcon = status?.icon;
          
          return (
            <div key={docType} className="flex items-center justify-between bg-white rounded p-2 text-sm">
              <div className="flex items-center gap-2 flex-1">
                <span className="text-gray-700 font-medium">{getDocumentName(docType)}</span>
                {doc ? (
                  <div className="flex items-center gap-2">
                    {status && <StatusIcon className={`w-4 h-4 ${status.color}`} />}
                    <span className={`text-xs ${status?.color}`}>{status?.text}</span>
                  </div>
                ) : (
                  <span className="text-xs text-gray-400">{t('driver_doc_not_uploaded')}</span>
                )}
              </div>
              {doc && doc.file_url && (
                <a 
                  href={`${BACKEND_URL}${doc.file_url}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 text-xs"
                >
                  {t('driver_view')}
                </a>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Filter orders based on service filters
  const filteredOrders = orders.filter(order => {
    // Filter by sub-tab: active or historical
    if (servicesSubTab === 'active') {
      // En Proceso: servicios que NO están completados, entregados o cancelados (excepto Cancelado en Transporte - Pendiente que sí debe aparecer)
      const isCancelled = order.status && (
        order.status.includes('Cancelado') || 
        order.status === 'cancelled'
      );
      const isPendingTransit = order.status === 'Cancelado en Transporte - Pendiente';
      
      if (order.status === 'delivered' || order.status === 'completed') return false;
      if (isCancelled && !isPendingTransit) return false; // Show pending transit in active
    } else if (servicesSubTab === 'historical') {
      // Histórico: servicios completados, entregados O cancelados (excepto Cancelado en Transporte - Pendiente)
      const isCancelled = order.status && (
        order.status.includes('Cancelado') || 
        order.status === 'cancelled'
      );
      const isPendingTransit = order.status === 'Cancelado en Transporte - Pendiente';
      
      if (isPendingTransit) return false; // Don't show pending transit in historical
      if (order.status !== 'delivered' && order.status !== 'completed' && !isCancelled) return false;
    }

    // Text search (direcciones de origen y destino, ciudad)
    if (serviceFilters.searchText) {
      const searchLower = serviceFilters.searchText.toLowerCase();
      const matchesAddress = 
        order.pickup_address?.toLowerCase().includes(searchLower) ||
        order.delivery_address?.toLowerCase().includes(searchLower);
      if (!matchesAddress) return false;
    }

    // Date range filter
    if (serviceFilters.dateFrom) {
      const orderDate = new Date(order.service_date);
      const filterDate = new Date(serviceFilters.dateFrom);
      if (orderDate < filterDate) return false;
    }
    if (serviceFilters.dateTo) {
      const orderDate = new Date(order.service_date);
      const filterDate = new Date(serviceFilters.dateTo);
      if (orderDate > filterDate) return false;
    }

    // Status filter
    if (serviceFilters.status !== 'all') {
      if (order.status !== serviceFilters.status) return false;
    }

    // Payment status filter
    if (serviceFilters.paymentStatus !== 'all') {
      const isPaid = order.payment_status === 'paid' || order.paid === true;
      if (serviceFilters.paymentStatus === 'paid' && !isPaid) return false;
      if (serviceFilters.paymentStatus === 'unpaid' && isPaid) return false;
    }

    // Vehicle brand filter
    if (serviceFilters.vehicleBrand) {
      const brandMatch = order.car_brand?.toLowerCase().includes(serviceFilters.vehicleBrand.toLowerCase());
      if (!brandMatch) return false;
    }

    // Vehicle model filter
    if (serviceFilters.vehicleModel) {
      const modelMatch = order.car_model?.toLowerCase().includes(serviceFilters.vehicleModel.toLowerCase());
      if (!modelMatch) return false;
    }

    // Vehicle plate filter
    if (serviceFilters.vehiclePlate) {
      const plateMatch = order.car_plate?.toLowerCase().includes(serviceFilters.vehiclePlate.toLowerCase());
      if (!plateMatch) return false;
    }

    // Fuel type filter
    if (serviceFilters.fuelType !== 'all') {
      if (order.fuel_type?.toLowerCase() !== serviceFilters.fuelType.toLowerCase()) return false;
    }

    // Driver filter (for fleet owners)
    if (serviceFilters.driver !== 'all') {
      if (order.driver_id !== serviceFilters.driver) return false;
    }

    return true;
  });

  const clearFilters = () => {
    setServiceFilters({
      searchText: '',
      dateFrom: '',
      dateTo: '',
      status: 'all',
      paymentStatus: 'all',
      vehicleBrand: '',
      vehicleModel: '',
      vehiclePlate: '',
      fuelType: 'all',
      driver: 'all'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#36e961] mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-30">
        <div className="w-full px-3 sm:px-4 lg:px-6 py-2 sm:py-3">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2 sm:gap-3">
              <BrandLogo className="h-10 w-auto sm:h-12" />
            </div>
            
            <div className="flex items-center gap-2 sm:gap-4">
              <Button variant="outline" size="sm" onClick={toggleLanguage} className="hidden sm:flex">
                <Languages className="w-4 h-4 mr-2" />
                {(i18n.language || '').startsWith('es') ? 'EN' : 'ES'}
              </Button>
              <Button variant="outline" size="sm" onClick={toggleLanguage} className="sm:hidden p-2">
                <Languages className="w-4 h-4" />
              </Button>
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-gray-900">{driver?.name}</p>
                <p className="text-xs text-gray-600">{driver?.email}</p>
              </div>
              <Button variant="outline" size="sm" onClick={handleLogout} className="hidden sm:flex">
                <LogOut className="w-4 h-4 mr-2" />
                {t('logout')}
              </Button>
              <Button variant="outline" size="sm" onClick={handleLogout} className="sm:hidden p-2">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="w-full px-3 sm:px-4 lg:px-6 py-4 sm:py-6 lg:py-8">
        {!driver?.documents_validated && (
          <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-yellow-900">{t('driver_docs_pending_title')}</h3>
                <p className="text-sm text-yellow-800 mt-1">
                  {t('driver_docs_pending_text')}
                </p>
              </div>
            </div>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4 sm:space-y-6">
          {/* Mobile: Horizontal scrollable tabs */}
          <div className="sm:hidden overflow-x-auto scrollbar-hide -mx-3 px-3">
            <TabsList className="inline-flex w-auto min-w-full space-x-1 bg-transparent p-1">
              <TabsTrigger value="home" className="flex-shrink-0 data-[state=active]:bg-white">
                <Home className="w-4 h-4" />
              </TabsTrigger>
              <TabsTrigger value="orders" className="flex-shrink-0 data-[state=active]:bg-white">
                <Package className="w-4 h-4" />
              </TabsTrigger>
              <TabsTrigger value="incomplete" className="flex-shrink-0 data-[state=active]:bg-white">
                <AlertTriangle className="w-4 h-4" />
              </TabsTrigger>
              {driver?.is_fleet_driver && (
                <TabsTrigger value="fleet" className="flex-shrink-0 data-[state=active]:bg-white">
                  <Truck className="w-4 h-4" />
                </TabsTrigger>
              )}
              <TabsTrigger value="profile" className="flex-shrink-0 data-[state=active]:bg-white">
                <User className="w-4 h-4" />
              </TabsTrigger>
              <TabsTrigger value="invoices" className="flex-shrink-0 data-[state=active]:bg-white">
                <DollarSign className="w-4 h-4" />
              </TabsTrigger>
            </TabsList>
          </div>
          
          {/* Desktop: Grid tabs with labels */}
          <TabsList className={`hidden sm:grid w-full ${driver?.is_fleet_driver ? 'sm:grid-cols-6' : 'sm:grid-cols-5'} max-w-4xl`}>
            <TabsTrigger value="home" className="text-sm">
              <Home className="w-4 h-4 mr-2" />
              <span>{t('driver_nav_home')}</span>
            </TabsTrigger>
            <TabsTrigger value="orders" className="text-sm">
              <Package className="w-4 h-4 mr-2" />
              <span>{t('driver_nav_services')}</span>
            </TabsTrigger>
            <TabsTrigger value="incomplete" className="text-sm">
              <AlertTriangle className="w-4 h-4 mr-2" />
              <span>{t('driver_nav_complete')}</span>
            </TabsTrigger>
            {driver?.is_fleet_driver && (
              <TabsTrigger value="fleet" className="text-sm">
                <Truck className="w-4 h-4 mr-2" />
                <span>{t('driver_nav_fleet')}</span>
              </TabsTrigger>
            )}
            <TabsTrigger value="profile" className="text-sm">
              <User className="w-4 h-4 mr-2" />
              <span>{t('driver_nav_profile')}</span>
            </TabsTrigger>
            <TabsTrigger value="invoices" className="text-sm">
              <DollarSign className="w-4 h-4 mr-2" />
              <span>{t('driver_nav_billing')}</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="home">
            <DriverDashboardHome 
              driver={driver}
              orders={orders}
              documents={documents}
              onTabChange={setActiveTab}
            />
          </TabsContent>

          <TabsContent value="orders">
            <Card>
              <CardHeader>
                <CardTitle>{t('driver_services_title')}</CardTitle>
                <CardDescription>{t('driver_services_subtitle')}</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Sub-tabs for Active / Historical */}
                <div className="flex gap-2 mb-6 border-b">
                  <button
                    onClick={() => setServicesSubTab('active')}
                    className={`px-4 py-2 font-medium transition-colors relative ${
                      servicesSubTab === 'active'
                        ? 'text-[#36e961] border-b-2 border-[#36e961]'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {t('driver_services_in_progress')}
                    <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-yellow-100 text-yellow-800">
                      {orders.filter(o => {
                        const isCancelled = o.status && (o.status.includes('Cancelado') || o.status === 'cancelled');
                        return o.status !== 'delivered' && o.status !== 'completed' && !isCancelled;
                      }).length}
                    </span>
                  </button>
                  <button
                    onClick={() => setServicesSubTab('historical')}
                    className={`px-4 py-2 font-medium transition-colors relative ${
                      servicesSubTab === 'historical'
                        ? 'text-[#36e961] border-b-2 border-[#36e961]'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {t('driver_services_historical')}
                    <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">
                      {orders.filter(o => {
                        const isCancelled = o.status && (o.status.includes('Cancelado') || o.status === 'cancelled');
                        return o.status === 'delivered' || o.status === 'completed' || isCancelled;
                      }).length}
                    </span>
                  </button>
                </div>

                {/* Filters Section */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg border">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Filter className="w-4 h-4 text-gray-600" />
                      <h3 className="font-semibold text-sm">{t('driver_filters_title')}</h3>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={clearFilters}
                      className="h-7 text-xs"
                    >
                      <X className="w-3 h-3 mr-1" />
                      {t('driver_clear')}
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                    {/* Search by address/city */}
                    <div>
                      <Label className="text-xs mb-1">{t('driver_search_address')}</Label>
                      <div className="relative">
                        <Search className="absolute left-2 top-2.5 w-4 h-4 text-gray-400" />
                        <Input
                          placeholder={t('ph_cities_search')}
                          value={serviceFilters.searchText}
                          onChange={(e) => setServiceFilters({...serviceFilters, searchText: e.target.value})}
                          className="pl-8 h-9 text-sm"
                        />
                      </div>
                    </div>

                    {/* Date From */}
                    <div>
                      <Label className="text-xs mb-1">{t('date_from')}</Label>
                      <Input
                        type="date"
                        value={serviceFilters.dateFrom}
                        onChange={(e) => setServiceFilters({...serviceFilters, dateFrom: e.target.value})}
                        className="h-9 text-sm"
                      />
                    </div>

                    {/* Date To */}
                    <div>
                      <Label className="text-xs mb-1">{t('date_to')}</Label>
                      <Input
                        type="date"
                        value={serviceFilters.dateTo}
                        onChange={(e) => setServiceFilters({...serviceFilters, dateTo: e.target.value})}
                        className="h-9 text-sm"
                      />
                    </div>

                    {/* Status */}
                    <div>
                      <Label className="text-xs mb-1">{t('driver_status')}</Label>
                      <Select 
                        value={serviceFilters.status} 
                        onValueChange={(value) => setServiceFilters({...serviceFilters, status: value})}
                      >
                        <SelectTrigger className="h-9 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t('driver_all')}</SelectItem>
                          <SelectItem value="assigned">{t('assigned')}</SelectItem>
                          <SelectItem value="in_progress">{t('in_progress')}</SelectItem>
                          <SelectItem value="completed">{t('driver_complete_status_done')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>


                    {/* Driver filter (only for fleet owners) */}
                    {driver.is_fleet_driver && (
                      <div>
                        <Label className="text-xs mb-1">{t('driver_driver_label')}</Label>
                        <Select 
                          value={serviceFilters.driver} 
                          onValueChange={(value) => setServiceFilters({...serviceFilters, driver: value})}
                        >
                          <SelectTrigger className="h-9 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">{t('driver_all')}</SelectItem>
                            <SelectItem value={driver.id}>{t('driver_myself')}</SelectItem>
                            {fleetMembers.map((member) => (
                              <SelectItem key={member.id} value={member.id}>
                                {member.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}


                    {/* Vehicle Brand */}
                    <div>
                      <Label className="text-xs mb-1">{t('driver_vehicle_brand')}</Label>
                      <Input
                        placeholder={t('transport_tab_ph_brand')}
                        value={serviceFilters.vehicleBrand}
                        onChange={(e) => setServiceFilters({...serviceFilters, vehicleBrand: e.target.value})}
                        className="h-9 text-sm"
                      />
                    </div>

                    {/* Vehicle Model */}
                    <div>
                      <Label className="text-xs mb-1">{t('driver_vehicle_model')}</Label>
                      <Input
                        placeholder={t('transport_tab_ph_model')}
                        value={serviceFilters.vehicleModel}
                        onChange={(e) => setServiceFilters({...serviceFilters, vehicleModel: e.target.value})}
                        className="h-9 text-sm"
                      />
                    </div>

                    {/* Vehicle Plate */}
                    <div>
                      <Label className="text-xs mb-1">{t('driver_plate')}</Label>
                      <Input
                        placeholder={t('transport_tab_ph_plate')}
                        value={serviceFilters.vehiclePlate}
                        onChange={(e) => setServiceFilters({...serviceFilters, vehiclePlate: e.target.value})}
                        className="h-9 text-sm"
                      />
                    </div>

                    {/* Fuel Type */}
                    <div>
                      <Label className="text-xs mb-1">{t('driver_fuel_type')}</Label>
                      <Select 
                        value={serviceFilters.fuelType} 
                        onValueChange={(value) => setServiceFilters({...serviceFilters, fuelType: value})}
                      >
                        <SelectTrigger className="h-9 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t('driver_all')}</SelectItem>
                          <SelectItem value="gasolina">{t('driver_fuel_gasoline')}</SelectItem>
                          <SelectItem value="diesel">{t('driver_fuel_diesel')}</SelectItem>
                          <SelectItem value="electrico">{t('driver_fuel_electric')}</SelectItem>
                          <SelectItem value="hibrido">{t('driver_fuel_hybrid')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Payment Status */}
                    <div>
                      <Label className="text-xs mb-1">{t('driver_payment_status')}</Label>
                      <Select 
                        value={serviceFilters.paymentStatus} 
                        onValueChange={(value) => setServiceFilters({...serviceFilters, paymentStatus: value})}
                      >
                        <SelectTrigger className="h-9 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t('driver_all')}</SelectItem>
                          <SelectItem value="paid">{t('driver_paid')}</SelectItem>
                          <SelectItem value="unpaid">{t('driver_unpaid')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Results count */}
                  <div className="mt-3 text-xs text-gray-600">
                    {t('driver_showing_services', { shown: filteredOrders.length, total: orders.length })}
                  </div>
                </div>

                {filteredOrders.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Package className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                    <p className="font-semibold mb-1">
                      {orders.length === 0 ? t('driver_no_orders') : t('driver_no_orders_filter')}
                    </p>
                    {orders.length > 0 && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={clearFilters}
                        className="mt-2"
                      >
                        {t('driver_clear_filters')}
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredOrders.map((order) => {
                      const isOverdue = isDeliveryDatePassed(order) && (order.status === 'assigned' || order.status === 'in_progress');
                      const isExpanded = expandedOrderId === order.id;
                      
                      return (
                        <div 
                          key={order.id} 
                          className={`bg-white rounded-xl shadow-sm border overflow-hidden ${isOverdue ? 'border-l-4 border-red-500' : ''}`}
                        >
                          {/* Card Header - Always Visible */}
                          <div 
                            className="p-4 cursor-pointer active:bg-gray-50"
                            onClick={() => setExpandedOrderId(isExpanded ? null : order.id)}
                          >
                            {/* Top Row: Date, Price, Status */}
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-gray-600">
                                  {order.pickup_date_from 
                                    ? new Date(order.pickup_date_from).toLocaleDateString(dateLocale, { day: '2-digit', month: 'short' })
                                    : (order.service_date ? new Date(order.service_date).toLocaleDateString(dateLocale, { day: '2-digit', month: 'short' }) : t('driver_date_unavailable'))
                                  }
                                </span>
                                {order.pickup_time_start && (
                                  <span className="text-xs text-gray-400">{order.pickup_time_start}</span>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                {/* Hide price for fleet members (fleet_driver_id means they work for someone else) */}
                                {!driver?.fleet_driver_id && (
                                  <span className="text-lg font-bold text-[#36e961]">€{formatEuro(order.driver_price, 0)}</span>
                                )}
                                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                  order.status === 'completed' || order.status === 'delivered' ? 'bg-green-100 text-green-700' :
                                  order.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                                  order.status === 'assigned' ? 'bg-yellow-100 text-yellow-700' :
                                  order.status?.includes('Cancelado') ? 'bg-red-100 text-red-700' :
                                  'bg-gray-100 text-gray-700'
                                }`}>
                                  {order.status === 'completed' || order.status === 'delivered' ? '✓' :
                                   order.status === 'in_progress' ? '→' :
                                   order.status === 'assigned' ? '●' : 
                                   order.status?.includes('Cancelado') ? '✕' : '?'}
                                </span>
                              </div>
                            </div>
                            
                            {/* Route */}
                            <div className="flex items-center gap-2 mb-2">
                              <div className="flex flex-col items-center">
                                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                                <div className="w-0.5 h-4 bg-gray-300"></div>
                                <div className="w-2 h-2 rounded-full bg-red-500"></div>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{order.pickup_address?.split(',')[0] || t('client_gps_route_origin')}</p>
                                <p className="text-sm text-gray-500 truncate">{order.delivery_address?.split(',')[0] || t('client_gps_route_destination')}</p>
                              </div>
                            </div>
                            
                            {/* Vehicle */}
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-mono font-bold bg-gray-100 px-2 py-1 rounded">{order.car_plate}</span>
                                <span className="text-xs text-gray-500">{order.car_brand} {order.car_model}</span>
                              </div>
                              <span className="text-xs text-gray-400">{order.distance_km ? `${formatEuro(order.distance_km, 0)} km` : ''}</span>
                            </div>
                            
                            {/* Overdue Warning */}
                            {isOverdue && (
                              <div className="mt-2 bg-red-100 text-red-700 px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1">
                                <AlertTriangle className="w-3 h-3" />
                                {t('driver_overdue_banner')}
                              </div>
                            )}
                          </div>
                          
                          {/* Expanded Content */}
                          {isExpanded && (
                            <div className="border-t bg-gray-50 p-4 space-y-4">
                              {/* Full Addresses */}
                              <div className="space-y-2">
                                <div className="flex items-start gap-2">
                                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <span className="text-green-600 text-xs">A</span>
                                  </div>
                                  <div>
                                    <p className="text-xs text-gray-500">{t('pickup')}</p>
                                    <p className="text-sm">{order.pickup_address || t('client_date_not_specified')}</p>
                                    {order.pickup_date_from && (
                                      <p className="text-xs text-gray-500 mt-1">
                                        {new Date(order.pickup_date_from).toLocaleDateString(dateLocale, { weekday: 'short', day: 'numeric', month: 'short' })}
                                        {order.pickup_time_start && ` • ${order.pickup_time_start}`}
                                        {order.pickup_time_end && ` - ${order.pickup_time_end}`}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-start gap-2">
                                  <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <span className="text-red-600 text-xs">B</span>
                                  </div>
                                  <div>
                                    <p className="text-xs text-gray-500">{t('delivery')}</p>
                                    <p className="text-sm">{order.delivery_address || t('client_date_not_specified')}</p>
                                    {order.delivery_date_from && (
                                      <p className="text-xs text-gray-500 mt-1">
                                        {new Date(order.delivery_date_from).toLocaleDateString(dateLocale, { weekday: 'short', day: 'numeric', month: 'short' })}
                                        {order.delivery_time_start && ` • ${order.delivery_time_start}`}
                                        {order.delivery_time_end && ` - ${order.delivery_time_end}`}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                              
                              {/* Contact Info */}
                              {(order.pickup_contact_name || order.delivery_contact_name) && (
                                <div className="grid grid-cols-2 gap-3 text-sm">
                                  {order.pickup_contact_name && (
                                    <div>
                                      <p className="text-xs text-gray-500">{t('pickup_contact')}</p>
                                      <p className="font-medium">{order.pickup_contact_name}</p>
                                      {order.pickup_contact_phone && (
                                        <a href={`tel:${order.pickup_contact_phone}`} className="text-blue-600 text-xs">
                                          {order.pickup_contact_phone}
                                        </a>
                                      )}
                                    </div>
                                  )}
                                  {order.delivery_contact_name && (
                                    <div>
                                      <p className="text-xs text-gray-500">{t('delivery_contact')}</p>
                                      <p className="font-medium">{order.delivery_contact_name}</p>
                                      {order.delivery_contact_phone && (
                                        <a href={`tel:${order.delivery_contact_phone}`} className="text-blue-600 text-xs">
                                          {order.delivery_contact_phone}
                                        </a>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}
                              
                              {/* Notes */}
                              {order.notes && (
                                <div className="bg-yellow-50 rounded-lg p-3">
                                  <p className="text-xs text-yellow-700 font-medium mb-1">{t('transport_tab_notes')}</p>
                                  <p className="text-sm text-yellow-900">{order.notes}</p>
                                </div>
                              )}
                              
                              {/* Action Buttons */}
                              <div className="flex flex-wrap gap-2 pt-2">
                                {/* Download Contract Button - Always visible */}
                                <Button
                                  variant="outline"
                                  className="flex-1 min-w-[120px]"
                                  disabled={downloadingContractOrderId != null}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDownloadContract(order.id);
                                  }}
                                >
                                  <Download className="w-4 h-4 mr-1" />
                                  {t('driver_download_contract')}
                                </Button>
                                
                                {(order.status === 'assigned' || order.status === 'in_progress') && (
                                  <>
                                    {order.status === 'assigned' && (
                                      <Button
                                        className="flex-1 min-w-[100px] bg-blue-600 hover:bg-blue-700 text-white"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleStartService(order.id);
                                        }}
                                      >
                                        {t('driver_action_start')}
                                      </Button>
                                    )}
                                    <Button
                                      className="flex-1 min-w-[100px] bg-green-600 hover:bg-green-700 text-white"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedOrderForCompletion(order);
                                        setShowCompletionForm(true);
                                      }}
                                    >
                                      {t('driver_complete_service')}
                                    </Button>
                                    {/* Report Incident Button */}
                                    <Button
                                      variant="outline"
                                      className="flex-1 min-w-[100px] border-red-300 text-red-600 hover:bg-red-50"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedOrderForIncident(order);
                                        setShowIncidentDialog(true);
                                      }}
                                    >
                                      <AlertTriangle className="w-4 h-4 mr-1" />
                                      {t('client_incident_badge')}
                                    </Button>
                                  </>
                                )}
                                <Button
                                  variant="outline"
                                  className="flex-1 min-w-[80px]"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedOrderId(null);
                                  }}
                                >
                                  {t('driver_close')}
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

            <TabsContent value="fleet">
              <div className="space-y-6">
                {/* Header with Create Button */}
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-2xl font-bold">{t('driver_nav_fleet')}</h2>
                    <p className="text-gray-600 mt-1">{t('driver_fleet_manage_subtitle')}</p>
                  </div>
                  <Button onClick={() => setShowCreateMemberDialog(true)}>
                    <User className="w-4 h-4 mr-2" />
                    {t('add_driver')}
                  </Button>
                </div>

                {/* Fleet Members List */}
                <Card>
                  <CardHeader>
                    <CardTitle>{t('driver_fleet_members_title')}</CardTitle>
                    <CardDescription>
                      {fleetMembers.length === 1
                        ? t('driver_fleet_members_count', { count: fleetMembers.length })
                        : t('driver_fleet_members_count_plural', { count: fleetMembers.length })}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {fleetMembers.length === 0 ? (
                      <div className="text-center py-12 text-gray-500">
                        <User className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                        <p className="text-lg font-medium">{t('driver_fleet_no_members')}</p>
                        <p className="text-sm mt-1">{t('driver_fleet_add_to_start')}</p>
                        <Button className="mt-4" onClick={() => setShowCreateMemberDialog(true)}>
                          {t('driver_fleet_add_first')}
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {fleetMembers.map((member) => (
                          <div key={member.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="flex items-center gap-3">
                                  <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                                    <User className="w-6 h-6 text-gray-600" />
                                  </div>
                                  <div>
                                    <h3 className="font-semibold text-lg">{member.name}</h3>
                                    <p className="text-sm text-gray-600">{member.email}</p>
                                  </div>
                                </div>
                                
                                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                  <div>
                                    <span className="text-gray-600">{t('driver_label_phone_short')}</span>
                                    <p className="font-medium">{member.phone}</p>
                                  </div>
                                  <div>
                                    <span className="text-gray-600">{t('driver_label_license_no')}</span>
                                    <p className="font-medium">{member.license_number}</p>
                                  </div>
                                  <div>
                                    <span className="text-gray-600">{t('driver_label_status')}</span>
                                    <p className="font-medium">
                                      <span className={`inline-flex items-center px-2 py-1 rounded text-xs ${
                                        member.status === 'available' ? 'bg-green-100 text-green-800' :
                                        member.status === 'busy' ? 'bg-yellow-100 text-yellow-800' :
                                        'bg-gray-100 text-gray-800'
                                      }`}>
                                        {member.status === 'available' ? t('driver_status_available') :
                                         member.status === 'busy' ? t('driver_status_busy') : t('driver_status_disconnected')}
                                      </span>
                                    </p>
                                  </div>
                                  <div>
                                    <span className="text-gray-600">{t('driver_label_documents')}</span>
                                    <p className="font-medium">
                                      <span className={`inline-flex items-center px-2 py-1 rounded text-xs ${
                                        member.documents_validated ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                      }`}>
                                        {member.documents_validated ? (
                                          <>
                                            <CheckCircle className="w-3 h-3 mr-1" />
                                            {t('driver_doc_approved')}
                                          </>
                                        ) : (
                                          <>
                                            <Clock className="w-3 h-3 mr-1" />
                                            {t('driver_doc_status_pending')}
                                          </>
                                        )}
                                      </span>
                                    </p>
                                  </div>
                                </div>
                              </div>
                              <div>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleEditMember(member)}
                                >
                                  <Edit className="w-4 h-4 mr-1" />
                                  {t('edit')}
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

          <TabsContent value="profile">
            <Card>
              <CardHeader>
                <CardTitle>{t('driver_profile_title')}</CardTitle>
                <CardDescription>{t('driver_profile_subtitle')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{t('driver_label_name')}</Label>
                    <p className="mt-1 text-gray-900">{driver?.name}</p>
                  </div>
                  <div>
                    <Label>{t('admin_email_header')}</Label>
                    <p className="mt-1 text-gray-900">{driver?.email}</p>
                  </div>
                  <div>
                    <Label>{t('driver_label_phone')}</Label>
                    <p className="mt-1 text-gray-900">{driver?.phone}</p>
                  </div>
                  <div>
                    <Label>{t('driver_label_dni')}</Label>
                    <p className="mt-1 text-gray-900">{driver?.license_number}</p>
                  </div>
                  <div>
                    <Label>{t('driver_label_vehicle')}</Label>
                    <p className="mt-1 text-gray-900">{driver?.vehicle_info || t('driver_not_specified')}</p>
                  </div>
                  <div>
                    <Label>{t('driver_label_status')}</Label>
                    <p className="mt-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        driver?.is_available ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {driver?.is_available ? t('driver_status_available') : t('driver_status_not_available')}
                      </span>
                    </p>
                  </div>
                </div>
                
                <div className="pt-4 border-t">
                  <Label>{t('driver_doc_validation')}</Label>
                  <div className="mt-2 flex items-center gap-2">
                    {driver?.documents_validated ? (
                      <span className="flex items-center gap-2 text-green-600">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-medium">{t('admin_documents_validated')}</span>
                      </span>
                    ) : (
                      <span className="flex items-center gap-2 text-yellow-600">
                        <Clock className="w-5 h-5" />
                        <span className="font-medium">{t('driver_validation_pending')}</span>
                      </span>
                    )}
                  </div>
                </div>

                {/* Billing Information Section */}
                <div className="pt-4 border-t">
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <Label className="text-lg font-semibold">{t('driver_billing_title')}</Label>
                      <p className="text-sm text-gray-600 mt-1">
                        {t('driver_billing_description')}
                      </p>
                    </div>
                    {!isEditingProfile ? (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={startEditingProfile}
                        className="text-blue-600 border-blue-600 hover:bg-blue-50"
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        {t('edit')}
                      </Button>
                    ) : (
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setIsEditingProfile(false)}
                          disabled={savingProfile}
                        >
                          {t('cancel')}
                        </Button>
                        <Button 
                          size="sm"
                          onClick={saveProfileChanges}
                          disabled={savingProfile}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          {savingProfile ? t('driver_saving') : t('driver_save_changes')}
                        </Button>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label>{t('driver_billing_full_name')} *</Label>
                      {isEditingProfile ? (
                        <Input
                          value={profileForm.full_name}
                          onChange={(e) => setProfileForm({...profileForm, full_name: e.target.value})}
                          placeholder={t('admin_billing_full_name_placeholder')}
                          className="mt-1"
                        />
                      ) : (
                        <p className="mt-1 text-gray-900">{driver?.full_name || t('driver_not_specified')}</p>
                      )}
                    </div>
                    <div>
                      <Label>{t('driver_billing_dni')} *</Label>
                      {isEditingProfile ? (
                        <Input
                          value={profileForm.dni_nie}
                          onChange={(e) => setProfileForm({...profileForm, dni_nie: e.target.value})}
                          placeholder="12345678A"
                          className="mt-1"
                        />
                      ) : (
                        <p className="mt-1 text-gray-900">{driver?.dni_nie || t('driver_not_specified')}</p>
                      )}
                    </div>
                    <div>
                      <Label>{t('driver_billing_irpf')} *</Label>
                      {isEditingProfile ? (
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          value={profileForm.irpf_retention}
                          onChange={(e) => setProfileForm({...profileForm, irpf_retention: parseFloat(e.target.value) || 0})}
                          placeholder="15"
                          className="mt-1"
                        />
                      ) : (
                        <p className="mt-1 text-gray-900">{driver?.irpf_retention || 0}%</p>
                      )}
                    </div>
                    <div className="col-span-2">
                      <Label>{t('driver_billing_fiscal_address')} *</Label>
                      {isEditingProfile ? (
                        <Input
                          value={profileForm.fiscal_address}
                          onChange={(e) => setProfileForm({...profileForm, fiscal_address: e.target.value})}
                          placeholder={t('admin_billing_fiscal_placeholder')}
                          className="mt-1"
                        />
                      ) : (
                        <p className="mt-1 text-gray-900">{driver?.fiscal_address || t('driver_not_specified')}</p>
                      )}
                    </div>
                    <div>
                      <Label>{t('driver_billing_bank')} *</Label>
                      {isEditingProfile ? (
                        <Input
                          value={profileForm.bank_name}
                          onChange={(e) => setProfileForm({...profileForm, bank_name: e.target.value})}
                          placeholder={t('admin_billing_bank_placeholder')}
                          className="mt-1"
                        />
                      ) : (
                        <p className="mt-1 text-gray-900">{driver?.bank_name || t('driver_not_specified')}</p>
                      )}
                    </div>
                    <div>
                      <Label>{t('driver_billing_iban')} *</Label>
                      {isEditingProfile ? (
                        <div>
                          <Input
                            value={profileForm.bank_account}
                            onChange={(e) => setProfileForm({...profileForm, bank_account: e.target.value})}
                            placeholder={t('admin_billing_iban_placeholder')}
                            className="mt-1"
                          />
                          <p className="text-xs text-blue-600 mt-1">
                            {t('driver_billing_iban_note')}
                          </p>
                        </div>
                      ) : (
                        <p className="mt-1 text-gray-900">{driver?.bank_account || t('driver_not_specified')}</p>
                      )}
                    </div>
                  </div>
                  
                  {!isEditingProfile && (!driver?.full_name || !driver?.dni_nie || !driver?.fiscal_address || !driver?.bank_name || !driver?.bank_account) && (
                    <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <p className="text-sm text-yellow-800">
                        <AlertTriangle className="w-4 h-4 inline mr-2" />
                        {t('driver_billing_incomplete_warning')}
                      </p>
                    </div>
                  )}
                </div>

                {/* Documents Section */}
                <div className="pt-4 border-t">
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <Label className="text-lg font-semibold">{t('driver_my_documents')}</Label>
                      <p className="text-sm text-gray-600 mt-1">
                        {t('driver_docs_progress', { uploaded: getDocumentProgress().uploaded, total: getDocumentProgress().total })}
                      </p>
                    </div>
                    <Button onClick={() => setShowUploadDialog(true)} className="bg-[#36e961] hover:bg-[#2dd156]" size="sm">
                      <Upload className="w-4 h-4 mr-2" />
                      {t('driver_upload_document')}
                    </Button>
                  </div>
                  
                  <div className="space-y-3">
                    {requiredDocuments.map((docType) => {
                      const doc = documents.find(d => d.document_type === docType);
                      const status = doc ? getDocumentStatus(doc) : null;
                      const StatusIcon = status?.icon;
                      
                      return (
                        <div key={docType} className="border rounded-lg p-3">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <h3 className="font-medium text-gray-900 text-sm">{getDocumentName(docType)}</h3>
                              {doc ? (
                                <div className="mt-1 space-y-1">
                                  <div className="flex items-center gap-2">
                                    {status && <StatusIcon className={`w-4 h-4 ${status.color}`} />}
                                    <span className={`text-xs font-medium ${status?.color}`}>
                                      {status?.text}
                                    </span>
                                  </div>
                                  <p className="text-xs text-gray-600">
                                    {t('driver_doc_uploaded')} {new Date(doc.upload_date).toLocaleDateString(dateLocale)}
                                  </p>
                                  {doc.expiry_date && (
                                    <p className="text-xs text-gray-600">
                                      {t('driver_doc_expires')} {new Date(doc.expiry_date).toLocaleDateString(dateLocale)}
                                    </p>
                                  )}
                                  {doc.rejection_reason && (
                                    <div className="mt-1 p-2 bg-red-50 border border-red-200 rounded">
                                      <p className="text-xs text-red-800">
                                        <strong>{t('driver_rejection_reason')}</strong> {doc.rejection_reason}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <p className="mt-1 text-xs text-gray-500">{t('driver_doc_not_uploaded')}</p>
                              )}
                            </div>
                            {doc && (doc.status === 'rejected' || doc.status === 'expired') && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setUploadForm({
                                    ...uploadForm,
                                    document_type: docType
                                  });
                                  setShowUploadDialog(true);
                                }}
                                className="ml-2 h-6 text-xs"
                              >
                                {t('driver_update')}
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Fleet Members Documents - Only for fleet owners */}
                {driver?.is_fleet_driver && fleetMembers.length > 0 && (
                  <div className="pt-6 mt-6 border-t">
                    <Label className="text-lg font-semibold mb-4 block">{t('driver_fleet_docs_title')}</Label>
                    <div className="space-y-6">
                      {fleetMembers.map((member) => (
                        <div key={member.id} className="border rounded-lg p-4 bg-gray-50">
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <h3 className="font-semibold text-gray-900">{member.name}</h3>
                              <p className="text-sm text-gray-600">{member.email}</p>
                            </div>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              member.documents_validated ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {member.documents_validated ? t('driver_fleet_validated') : `⏳ ${t('driver_doc_status_pending')}`}
                            </span>
                          </div>
                          
                          {/* Load and display member documents */}
                          <MemberDocumentsSection memberId={member.id} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Documents Tab - will continue */}
          <TabsContent value="documents">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>{t('driver_my_documents')}</CardTitle>
                    <CardDescription>
                      {t('driver_docs_progress', { uploaded: getDocumentProgress().uploaded, total: getDocumentProgress().total })}
                    </CardDescription>
                  </div>
                  <Button onClick={() => setShowUploadDialog(true)} className="bg-[#36e961] hover:bg-[#2dd156]">
                    <Upload className="w-4 h-4 mr-2" />
                    {t('driver_upload_document')}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {requiredDocuments.map((docType) => {
                    const doc = documents.find(d => d.document_type === docType);
                    const status = doc ? getDocumentStatus(doc) : null;
                    const StatusIcon = status?.icon;
                    
                    return (
                      <div key={docType} className="border rounded-lg p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h3 className="font-medium text-gray-900">{getDocumentName(docType)}</h3>
                            {doc ? (
                              <div className="mt-2 space-y-1">
                                <div className="flex items-center gap-2">
                                  {status && <StatusIcon className={`w-4 h-4 ${status.color}`} />}
                                  <span className={`text-sm font-medium ${status?.color}`}>
                                    {status?.text}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-600">
                                  {t('driver_doc_uploaded')} {new Date(doc.upload_date).toLocaleDateString(dateLocale)}
                                </p>
                                {doc.expiry_date && (
                                  <p className="text-sm text-gray-600">
                                    {t('driver_doc_expires')} {new Date(doc.expiry_date).toLocaleDateString(dateLocale)}
                                  </p>
                                )}
                                {doc.rejection_reason && (
                                  <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
                                    <p className="text-sm text-red-800">
                                      <strong>{t('driver_rejection_reason')}</strong> {doc.rejection_reason}
                                    </p>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <p className="mt-1 text-sm text-gray-500">{t('driver_doc_not_uploaded')}</p>
                            )}
                          </div>
                          {doc && (doc.status === 'rejected' || doc.status === 'expired') && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setUploadForm({
                                  ...uploadForm,
                                  document_type: docType
                                });
                                setShowUploadDialog(true);
                              }}
                            >
                              {t('driver_update')}
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="invoices">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>{t('driver_billing_my_settlements')}</CardTitle>
                    <CardDescription>{t('driver_billing_subtitle')}</CardDescription>
                  </div>
                  <Button onClick={() => { loadBillingPeriods(); loadCurrentPeriod(); loadDriverInvoices(); }} disabled={loadingBilling || loadingCurrentPeriod || loadingDriverInvoices} size="sm">
                    {(loadingBilling || loadingCurrentPeriod || loadingDriverInvoices) ? t('loading') : t('gps_live_refresh')}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Info banner */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-blue-800">
                    {t('driver_billing_periods_info')}
                  </p>
                </div>

                <div className="border rounded-lg p-4 mb-6 bg-white">
                  <h3 className="font-semibold text-gray-800 mb-1">{t('driver_billing_issued_invoices_title')}</h3>
                  <p className="text-xs text-gray-500 mb-3">{t('driver_billing_issued_invoices_desc')}</p>
                  {loadingDriverInvoices ? (
                    <p className="text-sm text-gray-500">{t('loading')}</p>
                  ) : driverInvoices.length === 0 ? (
                    <p className="text-sm text-gray-500">{t('driver_billing_no_invoices_on_file')}</p>
                  ) : (
                    <ul className="space-y-2">
                      {driverInvoices.filter((inv) => inv.pdf_url || inv.invoice_number).map((inv) => {
                        const path = inv.pdf_url || `/api/invoices/download/${inv.invoice_number}`;
                        const href = `${BACKEND_URL}${path.startsWith('/') ? '' : '/'}${path}`;
                        const label = inv.invoice_number || inv.id;
                        const when = inv.created_at ? new Date(inv.created_at).toLocaleDateString(dateLocale) : '';
                        return (
                          <li key={inv.id || inv.invoice_number} className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 pb-2 last:border-0 last:pb-0">
                            <div>
                              <span className="font-mono text-sm font-medium text-gray-800">{label}</span>
                              {when ? <span className="text-xs text-gray-500 ml-2">{when}</span> : null}
                              {inv.total != null ? (
                                <span className="text-sm text-gray-700 ml-2">€{Number(inv.total).toFixed(2)}</span>
                              ) : null}
                            </div>
                            <Button size="sm" variant="outline" className="text-green-700 border-green-600 hover:bg-green-50" onClick={() => window.open(href, '_blank')}>
                              <FileText className="w-3 h-3 mr-2" />
                              {t('driver_billing_download_invoice')}
                            </Button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>

                {/* CURRENT PERIOD - EN CURSO */}
                {loadingCurrentPeriod ? (
                  <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4 mb-6">
                    <div className="flex items-center gap-3">
                      <div className="animate-spin w-5 h-5 border-2 border-green-500 border-t-transparent rounded-full"></div>
                      <span className="text-green-700">{t('driver_billing_loading_period')}</span>
                    </div>
                  </div>
                ) : currentPeriodData && currentPeriodData.has_current_period ? (
                  <div className="bg-green-50 border-2 border-green-400 rounded-lg p-4 mb-6 shadow-sm">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-lg">📊</span>
                        </div>
                        <div>
                          <h3 className="font-bold text-green-800 text-lg flex items-center gap-2">
                            {t('driver_billing_in_progress')}
                            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                          </h3>
                          <p className="text-sm text-green-600">
                            {currentPeriodData.period_type === 'quincena_1' ? t('driver_billing_fortnight_1') : t('driver_billing_fortnight_2')}: {new Date(currentPeriodData.period_start).toLocaleDateString(dateLocale)} - {new Date(currentPeriodData.period_end).toLocaleDateString(dateLocale)}
                          </p>
                        </div>
                      </div>
                      <span className="px-3 py-1 bg-green-200 text-green-800 rounded-full text-sm font-medium">
                        {t('driver_billing_payment_label')} {new Date(currentPeriodData.payment_start).toLocaleDateString(dateLocale)} - {new Date(currentPeriodData.payment_end).toLocaleDateString(dateLocale)}
                      </span>
                    </div>
                    
                    {/* Current period totals */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-4">
                      <div className="bg-white rounded-lg p-3 text-center border">
                        <p className="text-xs text-gray-500 mb-1">{t('driver_billing_services')}</p>
                        <p className="text-xl font-bold text-green-700">{currentPeriodData.total_trips}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 text-center border">
                        <p className="text-xs text-gray-500 mb-1">{t('driver_billing_base')}</p>
                        <p className="text-lg font-semibold text-gray-700">€{formatEuro(currentPeriodData.base_imponible, 2)}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 text-center border">
                        <p className="text-xs text-gray-500 mb-1">{t('driver_billing_iva')}</p>
                        <p className="text-lg font-semibold text-gray-600">€{formatEuro(currentPeriodData.iva_amount, 2)}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 text-center border">
                        <p className="text-xs text-gray-500 mb-1">{t('driver_billing_supplies')}</p>
                        <p className="text-lg font-semibold text-orange-600">€{formatEuro(currentPeriodData.gastos_suplidos, 2)}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 text-center border">
                        <p className="text-xs text-gray-500 mb-1">{t('driver_billing_irpf_label')} ({currentPeriodData.irpf_percentage || 0}%)</p>
                        <p className="text-lg font-semibold text-red-600">€{formatEuro(currentPeriodData.irpf_amount, 2)}</p>
                      </div>
                      <div className="bg-green-100 rounded-lg p-3 text-center border-2 border-green-400 col-span-2">
                        <p className="text-xs text-green-600 mb-1">{t('driver_billing_total_accumulated')}</p>
                        <p className="text-2xl font-bold text-green-700">€{formatEuro(currentPeriodData.net_amount, 2)}</p>
                      </div>
                    </div>
                    
                    {/* Fleet breakdown if fleet owner */}
                    {currentPeriodData.is_fleet_owner && currentPeriodData.fleet_breakdown && Object.keys(currentPeriodData.fleet_breakdown).length > 0 && (
                      <div className="bg-white rounded-lg p-3 mb-3 border">
                        <p className="text-sm font-semibold text-gray-700 mb-2">👥 {t('driver_billing_fleet_breakdown')}</p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {Object.values(currentPeriodData.fleet_breakdown).map((member, idx) => (
                            <div key={idx} className="bg-blue-50 rounded p-2 text-sm">
                              <p className="font-medium text-gray-800 truncate">{member.name}</p>
                              <p className="text-gray-600">{member.trips} {t('driver_billing_trips')} • €{formatEuro(member.total, 2)}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Toggle services details */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowCurrentPeriodDetails(!showCurrentPeriodDetails)}
                      className="w-full border-green-400 text-green-700 hover:bg-green-100"
                    >
                      {showCurrentPeriodDetails ? t('driver_billing_hide_services') : t('driver_billing_view_services', { count: currentPeriodData.total_trips })}
                    </Button>
                    
                    {/* Services details table */}
                    {showCurrentPeriodDetails && currentPeriodData.services && currentPeriodData.services.length > 0 && (
                      <div className="mt-4 overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-green-100">
                              <th className="text-left py-2 px-3 font-medium text-gray-700">{t('driver_table_date')}</th>
                              <th className="text-left py-2 px-3 font-medium text-gray-700">{t('driver_plate')}</th>
                              <th className="text-left py-2 px-3 font-medium text-gray-700">{t('driver_table_vehicle')}</th>
                              <th className="text-left py-2 px-3 font-medium text-gray-700">{t('driver_table_trip')}</th>
                              {currentPeriodData.is_fleet_owner && (
                                <th className="text-left py-2 px-3 font-medium text-gray-700">{t('driver_billing_driver')}</th>
                              )}
                              <th className="text-right py-2 px-3 font-medium text-gray-700">{t('driver_billing_rate')}</th>
                              <th className="text-right py-2 px-3 font-medium text-gray-700">{t('driver_billing_supplies')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {currentPeriodData.services.map((service, idx) => (
                              <tr key={service.id || idx} className="border-b hover:bg-green-50">
                                <td className="py-2 px-3">
                                  {service.pickup_date_from ? new Date(service.pickup_date_from).toLocaleDateString(dateLocale, { day: '2-digit', month: 'short' }) : '-'}
                                </td>
                                <td className="py-2 px-3">
                                  <div className="flex items-center gap-2">
                                    <span className="font-mono bg-gray-100 px-2 py-0.5 rounded">{service.car_plate || '-'}</span>
                                    {service.debug_in_other_period && (
                                      <span
                                        className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-800 border border-amber-300"
                                        title={
                                          (service.debug_other_period_refs || [])
                                            .map((ref) => `${ref.period_type || 'period'} ${ref.period_start} -> ${ref.period_end}`)
                                            .join(' | ') || 'Service also exists in another billing period'
                                        }
                                      >
                                        cross-period
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="py-2 px-3 text-gray-600">
                                  {service.car_brand} {service.car_model}
                                </td>
                                <td className="py-2 px-3">
                                  <div className="max-w-xs">
                                    <div className="flex items-center gap-1 text-xs">
                                      <span className="text-green-600">●</span>
                                      <span className="truncate" title={service.pickup_address}>{service.pickup_address?.split(',')[0] || '-'}</span>
                                    </div>
                                    <div className="flex items-center gap-1 text-xs mt-0.5">
                                      <span className="text-red-600">●</span>
                                      <span className="truncate" title={service.delivery_address}>{service.delivery_address?.split(',')[0] || '-'}</span>
                                    </div>
                                  </div>
                                </td>
                                {currentPeriodData.is_fleet_owner && (
                                  <td className="py-2 px-3 text-gray-700">{service.driver_name || '-'}</td>
                                )}
                                <td className="py-2 px-3 text-right font-medium text-gray-800">€{formatEuro(service.driver_price)}</td>
                                <td className="py-2 px-3 text-right text-orange-600">€{formatEuro(service.expenses)}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="bg-green-100 font-semibold">
                              <td colSpan={currentPeriodData.is_fleet_owner ? 5 : 4} className="py-2 px-3 text-right text-gray-700">{t('driver_billing_totals')}</td>
                              <td className="py-2 px-3 text-right text-gray-800">€{formatEuro(currentPeriodData.base_imponible, 2)}</td>
                              <td className="py-2 px-3 text-right text-orange-600">€{formatEuro(currentPeriodData.gastos_suplidos, 2)}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}
                  </div>
                ) : currentPeriodData && !currentPeriodData.has_current_period ? (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
                        <span className="text-gray-600 text-lg">📭</span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-700">{t('driver_billing_current_no_services')}</h3>
                        <p className="text-sm text-gray-500">{t('driver_billing_current_no_services_desc')}</p>
                      </div>
                    </div>
                  </div>
                ) : null}

                {/* HISTORICAL PERIODS */}
                <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  📅 {t('driver_billing_previous_periods')}
                </h3>

                {loadingBilling && billingPeriods.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
                    <p className="mt-3 text-gray-500">{t('driver_billing_loading_settlements')}</p>
                  </div>
                ) : billingPeriods.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                    <p className="font-semibold mb-1">{t('driver_billing_no_previous')}</p>
                    <p className="text-sm">{t('driver_billing_no_previous_desc')}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {billingPeriods.map((period) => {
                      const isPaid = period.status === 'paid';
                      const invoiceDownloadPath =
                        period.invoice_pdf_url ||
                        (period.invoice_number ? `/api/invoices/download/${period.invoice_number}` : null);
                      const canShowInvoiceBtn =
                        !!invoiceDownloadPath && (period.invoice_generated || period.invoice_number);

                      return (
                        <div 
                          key={period.id}
                          className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full font-medium">
                                  {period.period_type === 'quincena_1' ? t('driver_billing_fortnight_1') : t('driver_billing_fortnight_2')}
                                </span>
                                <h3 className="font-semibold text-sm">
                                  {new Date(period.period_start).toLocaleDateString(dateLocale)} - {new Date(period.period_end).toLocaleDateString(dateLocale)}
                                </h3>
                              </div>
                              <div className="text-xs text-gray-600">
                                <span className="font-semibold">{t('driver_billing_payment_label')}</span> {period.payment_start ? new Date(period.payment_start).toLocaleDateString(dateLocale) : '?'} - {period.payment_end ? new Date(period.payment_end).toLocaleDateString(dateLocale) : '?'}
                              </div>
                            </div>
                            <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              isPaid ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {isPaid ? t('driver_billing_paid') : t('driver_billing_status_pending')}
                            </div>
                          </div>

                          {/* Financial breakdown */}
                          <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-3 text-center">
                            <div className="bg-blue-50 rounded p-2">
                              <div className="text-xs text-gray-600">{t('driver_billing_services')}</div>
                              <div className="text-lg font-bold text-blue-700">{period.total_trips}</div>
                            </div>
                            <div className="bg-green-50 rounded p-2">
                              <div className="text-xs text-gray-600">{t('driver_billing_base')}</div>
                              <div className="text-sm font-bold text-green-700">€{formatEuro(period.base_imponible || period.total_revenue, 2)}</div>
                            </div>
                            <div className="bg-teal-50 rounded p-2">
                              <div className="text-xs text-gray-600">{t('driver_billing_iva')}</div>
                              <div className="text-sm font-bold text-teal-700">€{formatEuro(period.iva_amount || 0, 2)}</div>
                            </div>
                            <div className="bg-orange-50 rounded p-2">
                              <div className="text-xs text-gray-600">{t('driver_billing_supplies')}</div>
                              <div className="text-sm font-bold text-orange-700">€{formatEuro(period.gastos_suplidos || period.total_expenses, 2)}</div>
                            </div>
                            <div className="bg-red-50 rounded p-2">
                              <div className="text-xs text-gray-600">{t('driver_billing_irpf_label')}</div>
                              <div className="text-sm font-bold text-red-700">€{formatEuro(period.irpf_amount || 0, 2)}</div>
                            </div>
                            <div className="bg-purple-50 rounded p-2 border-2 border-purple-300">
                              <div className="text-xs text-gray-600">{t('driver_billing_grand_total_abbr')}</div>
                              <div className="text-lg font-bold text-purple-700">€{formatEuro(period.net_amount, 2)}</div>
                            </div>
                          </div>

                          {/* Fleet breakdown if exists */}
                          {period.fleet_breakdown && Object.keys(period.fleet_breakdown).length > 0 && (
                            <div className="bg-gray-50 rounded p-2 mb-3">
                              <p className="text-xs text-gray-600 mb-1">{t('driver_billing_period_breakdown')}</p>
                              <div className="flex flex-wrap gap-2">
                                {Object.entries(period.fleet_breakdown).map(([driverId, data]) => (
                                  <span key={driverId} className="text-xs bg-white px-2 py-1 rounded border">
                                    {data.driver_name}: {data.services?.length || 0} {t('driver_billing_services_count')}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          <div className="flex gap-2">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => viewPeriodDetails(period)}
                              className="flex-1"
                            >
                              <Eye className="w-3 h-3 mr-2" />
                              {t('driver_billing_view_services_btn')}
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => downloadLiquidationExcel(period.id)}
                              className="text-green-600 border-green-600 hover:bg-green-50"
                              data-testid={`download-excel-${period.id}`}
                            >
                              <Download className="w-3 h-3 mr-2" />
                              Excel
                            </Button>
                            {canShowInvoiceBtn && (
                              <Button 
                                size="sm"
                                onClick={() => window.open(`${BACKEND_URL}${invoiceDownloadPath}`, '_blank')}
                                className="bg-green-600 hover:bg-green-700 text-white"
                              >
                                <FileText className="w-3 h-3 mr-2" />
                                {t('driver_billing_download_invoice')}
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Period Details Dialog */}
      <Dialog open={showPeriodDialog} onOpenChange={setShowPeriodDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('driver_billing_period_details_title')}</DialogTitle>
            <DialogDescription>
              {selectedPeriod && (
                <>
                  {t('driver_billing_period_label')} {new Date(selectedPeriod.period_start).toLocaleDateString(dateLocale)} - {new Date(selectedPeriod.period_end).toLocaleDateString(dateLocale)}
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          {periodDetails && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="bg-blue-50 rounded p-3 text-center">
                  <div className="text-xs text-gray-600 mb-1">{t('driver_billing_total_trips')}</div>
                  <div className="text-2xl font-bold text-blue-700">{periodDetails.period.total_trips}</div>
                </div>
                <div className="bg-green-50 rounded p-3 text-center">
                  <div className="text-xs text-gray-600 mb-1">{t('driver_billing_income_without_vat')}</div>
                  <div className="text-2xl font-bold text-green-700">€{formatEuro(periodDetails.period.total_revenue, 2)}</div>
                </div>
                <div className="bg-teal-50 rounded p-3 text-center">
                  <div className="text-xs text-gray-600 mb-1">{t('driver_billing_iva')}</div>
                  <div className="text-2xl font-bold text-teal-700">€{formatEuro(periodDetails.period.total_revenue * 0.21)}</div>
                </div>
                <div className="bg-orange-50 rounded p-3 text-center">
                  <div className="text-xs text-gray-600 mb-1">{t('driver_billing_expenses_without_vat')}</div>
                  <div className="text-2xl font-bold text-orange-700">€{formatEuro(periodDetails.period.total_expenses, 2)}</div>
                </div>
                <div className="bg-purple-50 rounded p-3 text-center border-2 border-purple-300">
                  <div className="text-xs text-gray-600 mb-1">{t('driver_billing_total_to_pay')}</div>
                  <div className="text-2xl font-bold text-purple-700">
                    €{(
                      ((periodDetails.period.base_imponible ?? periodDetails.period.total_revenue ?? 0) || 0) +
                      ((periodDetails.period.iva_amount ?? ((periodDetails.period.base_imponible ?? periodDetails.period.total_revenue ?? 0) * 0.21) ?? 0) || 0) +
                      ((periodDetails.period.extras_base ?? 0) || 0) +
                      ((periodDetails.period.gastos_suplidos ?? periodDetails.period.total_expenses ?? 0) || 0) +
                      ((periodDetails.period.irpf_amount ?? 0) || 0)
                    ).toFixed(2)}
                  </div>
                  <div className="text-[9px] text-gray-500 mt-1">{t('driver_billing_total_to_pay_caption')}</div>
                </div>
              </div>

              {/* Orders List */}
              <div>
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  {t('driver_billing_completed_services_count', { count: periodDetails.orders?.length || 0 })}
                </h3>
                <div className="border rounded-lg overflow-hidden overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left">{t('driver_date_pickup')}</th>
                        <th className="px-3 py-2 text-left">{t('driver_date_delivery_label')}</th>
                        <th className="px-3 py-2 text-left">{t('driver_billing_driver')}</th>
                        <th className="px-3 py-2 text-left">{t('driver_origin_short')}</th>
                        <th className="px-3 py-2 text-left">{t('driver_destination_short')}</th>
                        <th className="px-3 py-2 text-left">{t('driver_plate')}</th>
                        <th className="px-3 py-2 text-left">{t('driver_table_vehicle')}</th>
                        <th className="px-3 py-2 text-right">{t('driver_table_price')}</th>
                        <th className="px-3 py-2 text-center">{t('driver_billing_column_expenses')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {periodDetails.orders?.map((order) => {
                        const expenseReport = periodDetails.expense_reports?.find(r => r.order_id === order.id);
                        
                        return (
                          <tr key={order.id} className="border-t hover:bg-gray-50">
                            <td className="px-3 py-2 whitespace-nowrap">
                              <div>
                                {order.pickup_date ? new Date(order.pickup_date).toLocaleDateString(dateLocale) : 
                                 order.service_date ? new Date(order.service_date).toLocaleDateString(dateLocale) : t('client_na')}
                              </div>
                              {(order.pickup_time_start || order.pickup_time_end) && (
                                <div className="text-gray-500 text-[10px] mt-0.5">
                                  🕐 {order.pickup_time_start && order.pickup_time_end 
                                    ? `${order.pickup_time_start} - ${order.pickup_time_end}`
                                    : order.pickup_time_start || order.pickup_time_end}
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              <div>
                                {order.delivery_date ? new Date(order.delivery_date).toLocaleDateString(dateLocale) : 
                                 order.delivery_margin_date ? new Date(order.delivery_margin_date).toLocaleDateString(dateLocale) :
                                 order.service_date ? new Date(order.service_date).toLocaleDateString(dateLocale) : t('client_na')}
                              </div>
                              {(order.delivery_time_start || order.delivery_time_end) && (
                                <div className="text-gray-500 text-[10px] mt-0.5">
                                  🕐 {order.delivery_time_start && order.delivery_time_end 
                                    ? `${order.delivery_time_start} - ${order.delivery_time_end}`
                                    : order.delivery_time_start || order.delivery_time_end}
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-2">{order.driver_name || t('client_na')}</td>
                            <td className="px-3 py-2">{order.pickup_address?.split(',')[0]}</td>
                            <td className="px-3 py-2">{order.delivery_address?.split(',')[0]}</td>
                            <td className="px-3 py-2 font-mono text-[10px]">{order.car_plate || t('client_na')}</td>
                            <td className="px-3 py-2 text-[10px]">
                              {order.car_brand && order.car_model ? `${order.car_brand} ${order.car_model}` : t('client_na')}
                            </td>
                            <td className="px-3 py-2 text-right font-semibold whitespace-nowrap">
                              €{formatEuro(order.driver_price, 2)}
                              {order.driver_pricing_template_id && (
                                <div className="text-[10px] text-gray-600">
                                  {driverPricingTemplates.find(t => t.id === order.driver_pricing_template_id)?.name}
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-2 text-center">
                              {expenseReport ? (
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap ${
                                  expenseReport.status === 'approved' ? 'bg-green-100 text-green-700' :
                                  expenseReport.status === 'rejected' ? 'bg-red-100 text-red-700' :
                                  'bg-yellow-100 text-yellow-700'
                                }`}>
                                  <Receipt className="w-3 h-3" />
                                  €{formatEuro(expenseReport.total_expense, 2)}
                                  {expenseReport.status === 'approved' && ' ✓'}
                                  {expenseReport.status === 'rejected' && ' ✗'}
                                  {expenseReport.status === 'pending' && ' ⏳'}
                                </span>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Penalties section */}
              {Array.isArray(periodDetails.penalties) && periodDetails.penalties.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-2 flex items-center gap-2 text-red-700">
                    <AlertTriangle className="w-4 h-4" />
                    Penalties ({periodDetails.penalties.length})
                  </h3>
                  <div className="border rounded-lg overflow-hidden overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-red-50">
                        <tr>
                          <th className="px-3 py-2 text-left">Date</th>
                          <th className="px-3 py-2 text-left">Reason</th>
                          <th className="px-3 py-2 text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {periodDetails.penalties.map((pen, idx) => (
                          <tr key={`${pen.id || idx}`} className="border-t hover:bg-red-50/40">
                            <td className="px-3 py-2 whitespace-nowrap">
                              {pen.created_at ? new Date(pen.created_at).toLocaleDateString(dateLocale) : '-'}
                            </td>
                            <td className="px-3 py-2">
                              <div className="font-medium text-gray-700">{pen.reason || 'Penalty'}</div>
                              {pen.description ? <div className="text-[10px] text-gray-500">{pen.description}</div> : null}
                            </td>
                            <td className="px-3 py-2 text-right font-semibold text-red-700 whitespace-nowrap">
                              €{Number(pen.amount || 0).toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-red-50 font-semibold">
                          <td colSpan={2} className="px-3 py-2 text-right text-red-800">Total penalties</td>
                          <td className="px-3 py-2 text-right text-red-800">
                            €{periodDetails.penalties.reduce((sum, p) => sum + Number(p.amount || 0), 0).toFixed(2)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}

              {/* Expense Reports Details */}
              {periodDetails.expense_reports && periodDetails.expense_reports.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-2 flex items-center gap-2">
                    <Receipt className="w-4 h-4" />
                    {t('driver_expense_details_title')}
                  </h3>
                  <div className="space-y-2">
                    {periodDetails.expense_reports.map((report) => {
                      const order = periodDetails.orders?.find(o => o.id === report.order_id);
                      const isExpanded = expandedReceipts[report.id] || false;
                      
                      return (
                        <div key={report.id} className="border rounded-lg p-3 text-xs">
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <div className="font-semibold">{order?.pickup_address?.split(',')[0]} → {order?.delivery_address?.split(',')[0]}</div>
                              <div className="text-gray-600">{new Date(order?.service_date).toLocaleDateString(dateLocale)}</div>
                            </div>
                            <span className={`px-2 py-1 rounded text-[10px] font-semibold ${
                              report.status === 'approved' ? 'bg-green-100 text-green-700' :
                              report.status === 'rejected' ? 'bg-red-100 text-red-700' :
                              'bg-yellow-100 text-yellow-700'
                            }`}>
                              {report.status === 'approved' ? t('driver_doc_status_approved') :
                               report.status === 'rejected' ? t('driver_doc_rejected') : t('driver_doc_status_pending')}
                            </span>
                          </div>
                          <div className="grid grid-cols-4 gap-2">
                            <div className="bg-gray-50 rounded p-2">
                              <div className="text-[10px] text-gray-600">{t('driver_billing_driver')}</div>
                              <div className="font-semibold">€{formatEuro(report.driver_expense, 2)}</div>
                            </div>
                            <div className="bg-gray-50 rounded p-2">
                              <div className="text-[10px] text-gray-600">{t('driver_expense_fuel')}</div>
                              <div className="font-semibold">€{formatEuro(report.fuel_expense, 2)}</div>
                            </div>
                            <div className="bg-gray-50 rounded p-2">
                              <div className="text-[10px] text-gray-600">{t('driver_expense_tolls')}</div>
                              <div className="font-semibold">€{formatEuro(report.toll_expense, 2)}</div>
                            </div>
                            <div className="bg-blue-50 rounded p-2 flex items-center justify-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setExpandedReceipts(prev => ({...prev, [report.id]: !prev[report.id]}))}
                                className="h-auto py-1 px-2 text-[10px] text-blue-700 hover:bg-blue-100"
                              >
                                <Eye className="w-3 h-3 mr-1" />
                                {t('driver_view_more')}
                              </Button>
                            </div>
                          </div>
                          
                          {/* Expanded Receipts Section */}
                          {isExpanded && (
                            <div className="mt-3 p-3 bg-gray-50 rounded border border-gray-200">
                              <h4 className="font-semibold text-[11px] mb-2 flex items-center gap-1">
                                <Image className="w-3 h-3" />
                                {t('driver_expense_tickets')}
                              </h4>
                              {report.receipts && report.receipts.length > 0 ? (
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                  {report.receipts.map((receipt, idx) => (
                                    <div key={idx} className="border rounded overflow-hidden bg-white">
                                      <img 
                                        src={receipt} 
                                        alt={t('driver_billing_ticket_number', { n: idx + 1 })}
                                        className="w-full h-32 object-cover cursor-pointer hover:opacity-80"
                                        onClick={() => window.open(receipt, '_blank')}
                                      />
                                      <div className="p-1 text-center text-[9px] text-gray-600">
                                        {t('driver_billing_ticket_number', { n: idx + 1 })}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="text-center py-4 text-gray-400 text-[10px]">
                                  {t('driver_billing_no_tickets_attached')}
                                </div>
                              )}
                              <div className="mt-2 grid grid-cols-2 gap-2 text-[10px]">
                                <div className="bg-white rounded p-2">
                                  <span className="text-gray-600">{t('driver_billing_total_parking')}</span>
                                  <span className="font-semibold ml-1">€{formatEuro(report.parking_expense, 2)}</span>
                                </div>
                                <div className="bg-white rounded p-2">
                                  <span className="text-gray-600">{t('driver_billing_total_expenses_short')}</span>
                                  <span className="font-semibold ml-1">€{formatEuro(report.total_expense, 2)}</span>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {report.status === 'rejected' && report.rejection_reason && (
                            <div className="mt-2 p-2 bg-red-50 rounded text-red-700">
                              <span className="font-semibold">{t('driver_rejection_reason')}</span> {report.rejection_reason}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Upload Document Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('driver_upload_document')}</DialogTitle>
            <DialogDescription>
              {t('driver_upload_dialog_desc')}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleDocumentUpload} className="space-y-4">
            <div>
              <Label>{t('driver_doc_type_label')}</Label>
              <select
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md"
                value={uploadForm.document_type}
                onChange={(e) => setUploadForm({ ...uploadForm, document_type: e.target.value })}
                required
              >
                <option value="">{t('driver_doc_type_placeholder')}</option>
                {requiredDocuments.map(docType => (
                  <option key={docType} value={docType}>
                    {getDocumentName(docType)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label>{t('driver_file_label')}</Label>
              <Input
                type="file"
                accept={uploadForm.document_type?.endsWith('_certificate') ? '.pdf' : 'image/*'}
                onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files[0] })}
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                {uploadForm.document_type?.endsWith('_certificate') 
                  ? t('driver_file_hint_pdf', { maxMB: 10 })
                  : t('driver_file_hint_image', { maxMB: 10 })}
              </p>
            </div>

            <div>
              <Label>{t('driver_upload_date_label')}</Label>
              <Input
                type="date"
                value={uploadForm.upload_date}
                onChange={(e) => setUploadForm({ ...uploadForm, upload_date: e.target.value })}
                required
              />
            </div>

            {(uploadForm.document_type === 'hacienda_certificate' || 
              uploadForm.document_type === 'ss_certificate') && (
              <div>
                <Label>{t('driver_expiry_date_label')}</Label>
                <Input
                  type="date"
                  value={uploadForm.expiry_date}
                  onChange={(e) => setUploadForm({ ...uploadForm, expiry_date: e.target.value })}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  {uploadForm.document_type === 'hacienda_certificate' 
                    ? t('driver_expiry_hint_hacienda')
                    : t('driver_expiry_hint_ss')}
                </p>
              </div>
            )}

            <div className="flex gap-3 justify-end pt-4">
              <Button type="button" variant="outline" onClick={() => setShowUploadDialog(false)}>
                {t('cancel')}
              </Button>
              <Button type="submit" disabled={uploadingDoc} className="bg-[#36e961] hover:bg-[#2dd156]">
                {uploadingDoc ? t('driver_uploading') : t('driver_upload_btn')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Fleet Member Dialog */}
      {driver?.is_fleet_driver && (
        <Dialog open={showCreateMemberDialog} onOpenChange={setShowCreateMemberDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('driver_dialog_add_fleet_title')}</DialogTitle>
              <DialogDescription>
                {t('driver_dialog_add_fleet_desc')}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateFleetMember} className="space-y-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="font-semibold">{t('driver_dialog_basic_info')}</h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="member_name">{t('driver_dialog_full_name')} *</Label>
                    <Input
                      id="member_name"
                      type="text"
                      value={createMemberForm.name}
                      onChange={(e) => setCreateMemberForm({ ...createMemberForm, name: e.target.value })}
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="member_email">{t('admin_email_required')}</Label>
                    <Input
                      id="member_email"
                      type="email"
                      value={createMemberForm.email}
                      onChange={(e) => setCreateMemberForm({ ...createMemberForm, email: e.target.value })}
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="member_phone">{t('driver_label_phone')} *</Label>
                    <Input
                      id="member_phone"
                      type="tel"
                      value={createMemberForm.phone}
                      onChange={(e) => setCreateMemberForm({ ...createMemberForm, phone: e.target.value })}
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="member_license">{t('driver_dialog_license_no')} *</Label>
                    <Input
                      id="member_license"
                      type="text"
                      value={createMemberForm.license_number}
                      onChange={(e) => setCreateMemberForm({ ...createMemberForm, license_number: e.target.value })}
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="member_dni_nie">{t('driver_billing_dni')} *</Label>
                    <Input
                      id="member_dni_nie"
                      type="text"
                      value={createMemberForm.dni_nie}
                      onChange={(e) => setCreateMemberForm({ ...createMemberForm, dni_nie: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-700">
                    {t('driver_dialog_password_note')}
                  </p>
                </div>
              </div>

              {/* Document Upload Section */}
              <div className="space-y-4">
                <h3 className="font-semibold">{t('driver_dialog_docs_optional')}</h3>
                <p className="text-sm text-gray-600">
                  {t('driver_dialog_docs_later')}
                </p>
                
                <div className="grid grid-cols-2 gap-4">
                  {/* License Front */}
                  <div>
                    <Label htmlFor="license_front">{getDocumentName('license_front')}</Label>
                    <div className="mt-2">
                      <input
                        id="license_front"
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => setMemberDocuments({ ...memberDocuments, license_front: e.target.files[0] })}
                        className="w-full text-sm"
                      />
                      {memberDocuments.license_front && (
                        <p className="text-xs text-green-600 mt-1">
                          <CheckCircle className="w-3 h-3 inline mr-1" />
                          {memberDocuments.license_front.name}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* License Back */}
                  <div>
                    <Label htmlFor="license_back">{getDocumentName('license_back')}</Label>
                    <div className="mt-2">
                      <input
                        id="license_back"
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => setMemberDocuments({ ...memberDocuments, license_back: e.target.files[0] })}
                        className="w-full text-sm"
                      />
                      {memberDocuments.license_back && (
                        <p className="text-xs text-green-600 mt-1">
                          <CheckCircle className="w-3 h-3 inline mr-1" />
                          {memberDocuments.license_back.name}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* DNI Front */}
                  <div>
                    <Label htmlFor="dni_front">{getDocumentName('dni_front')}</Label>
                    <div className="mt-2">
                      <input
                        id="dni_front"
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => setMemberDocuments({ ...memberDocuments, dni_front: e.target.files[0] })}
                        className="w-full text-sm"
                      />
                      {memberDocuments.dni_front && (
                        <p className="text-xs text-green-600 mt-1">
                          <CheckCircle className="w-3 h-3 inline mr-1" />
                          {memberDocuments.dni_front.name}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* DNI Back */}
                  <div>
                    <Label htmlFor="dni_back">{getDocumentName('dni_back')}</Label>
                    <div className="mt-2">
                      <input
                        id="dni_back"
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => setMemberDocuments({ ...memberDocuments, dni_back: e.target.files[0] })}
                        className="w-full text-sm"
                      />
                      {memberDocuments.dni_back && (
                        <p className="text-xs text-green-600 mt-1">
                          <CheckCircle className="w-3 h-3 inline mr-1" />
                          {memberDocuments.dni_back.name}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 justify-end pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setShowCreateMemberDialog(false)}>
                  {t('cancel')}
                </Button>
                <Button type="submit" className="bg-[#36e961] hover:bg-[#2dd156]">
                  {t('driver_dialog_create_driver')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Fleet Member Dialog */}
      {driver?.is_fleet_driver && editingMember && (
        <Dialog open={showEditMemberDialog} onOpenChange={setShowEditMemberDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('driver_dialog_edit_fleet_title')}</DialogTitle>
              <DialogDescription>
                {t('driver_dialog_edit_fleet_desc')}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpdateMember} className="space-y-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="font-semibold">{t('driver_dialog_basic_info')}</h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="edit_member_name">{t('driver_dialog_full_name')} *</Label>
                    <Input
                      id="edit_member_name"
                      type="text"
                      value={editingMember.name}
                      onChange={(e) => setEditingMember({ ...editingMember, name: e.target.value })}
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="edit_member_email">{t('admin_email_required')}</Label>
                    <Input
                      id="edit_member_email"
                      type="email"
                      value={editingMember.email}
                      onChange={(e) => setEditingMember({ ...editingMember, email: e.target.value })}
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="edit_member_phone">{t('driver_label_phone')} *</Label>
                    <Input
                      id="edit_member_phone"
                      type="tel"
                      value={editingMember.phone}
                      onChange={(e) => setEditingMember({ ...editingMember, phone: e.target.value })}
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="edit_member_license">{t('driver_dialog_license_no')} *</Label>
                    <Input
                      id="edit_member_license"
                      type="text"
                      value={editingMember.license_number}
                      onChange={(e) => setEditingMember({ ...editingMember, license_number: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <input
                    type="checkbox"
                    id="edit_member_available"
                    checked={editingMember.is_available}
                    onChange={(e) => setEditingMember({ ...editingMember, is_available: e.target.checked })}
                    className="h-4 w-4"
                  />
                  <Label htmlFor="edit_member_available" className="text-sm cursor-pointer">
                    {t('driver_dialog_driver_available')}
                  </Label>
                </div>
              </div>

              {/* Document Upload Section */}
              <div className="space-y-4">
                <h3 className="font-semibold">{t('driver_dialog_upload_section_title')}</h3>
                <p className="text-sm text-gray-600">
                  {t('driver_fleet_edit_docs_intro', { maxMB: 10 })}
                </p>
                
                <div className="grid grid-cols-2 gap-4">
                  {/* License Front */}
                  <div>
                    <Label htmlFor="edit_license_front">{getDocumentName('license_front')}</Label>
                    <div className="mt-2">
                      <input
                        id="edit_license_front"
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => setEditingMember({ 
                          ...editingMember, 
                          documents: { ...editingMember.documents, license_front: e.target.files[0] }
                        })}
                        className="w-full text-sm"
                      />
                      {editingMember.documents.license_front && (
                        <p className="text-xs text-green-600 mt-1">
                          <CheckCircle className="w-3 h-3 inline mr-1" />
                          {editingMember.documents.license_front.name}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* License Back */}
                  <div>
                    <Label htmlFor="edit_license_back">{getDocumentName('license_back')}</Label>
                    <div className="mt-2">
                      <input
                        id="edit_license_back"
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => setEditingMember({ 
                          ...editingMember, 
                          documents: { ...editingMember.documents, license_back: e.target.files[0] }
                        })}
                        className="w-full text-sm"
                      />
                      {editingMember.documents.license_back && (
                        <p className="text-xs text-green-600 mt-1">
                          <CheckCircle className="w-3 h-3 inline mr-1" />
                          {editingMember.documents.license_back.name}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* DNI Front */}
                  <div>
                    <Label htmlFor="edit_dni_front">{getDocumentName('dni_front')}</Label>
                    <div className="mt-2">
                      <input
                        id="edit_dni_front"
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => setEditingMember({ 
                          ...editingMember, 
                          documents: { ...editingMember.documents, dni_front: e.target.files[0] }
                        })}
                        className="w-full text-sm"
                      />
                      {editingMember.documents.dni_front && (
                        <p className="text-xs text-green-600 mt-1">
                          <CheckCircle className="w-3 h-3 inline mr-1" />
                          {editingMember.documents.dni_front.name}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* DNI Back */}
                  <div>
                    <Label htmlFor="edit_dni_back">{getDocumentName('dni_back')}</Label>
                    <div className="mt-2">
                      <input
                        id="edit_dni_back"
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => setEditingMember({ 
                          ...editingMember, 
                          documents: { ...editingMember.documents, dni_back: e.target.files[0] }
                        })}
                        className="w-full text-sm"
                      />
                      {editingMember.documents.dni_back && (
                        <p className="text-xs text-green-600 mt-1">
                          <CheckCircle className="w-3 h-3 inline mr-1" />
                          {editingMember.documents.dni_back.name}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 justify-end pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => {
                  setShowEditMemberDialog(false);
                  setEditingMember(null);
                }}>
                  {t('cancel')}
                </Button>
                <Button type="submit" className="bg-[#36e961] hover:bg-[#2dd156]">
                  {t('driver_save_changes')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* Dialog para completar servicio - SIMPLIFICADO */}
      {showCompleteServiceDialog && selectedOrderToComplete && (
        <Dialog open={showCompleteServiceDialog} onOpenChange={setShowCompleteServiceDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('driver_complete_dialog_title', { id: selectedOrderToComplete.id?.substring(0, 8).toUpperCase() })}</DialogTitle>
              <DialogDescription>
                {selectedOrderToComplete.car_brand} {selectedOrderToComplete.car_model} - {selectedOrderToComplete.car_plate}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6">
              {/* SECCIÓN 1: Fotos de Recogida */}
              <div className="border rounded-lg p-4 bg-blue-50">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold flex items-center gap-2 text-blue-700">
                    <Image className="w-5 h-5" />
                    {t('driver_complete_section_pickup')}
                  </h3>
                  {selectedOrderToComplete.has_pickup_photos && (
                    <span className="flex items-center gap-1 text-green-600 text-sm font-medium">
                      <CheckCircle className="w-4 h-4" /> {t('driver_complete_status_done')}
                    </span>
                  )}
                </div>
                
                {!selectedOrderToComplete.has_pickup_photos ? (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-600">
                      {t('driver_complete_pickup_hint')}
                    </p>
                    <Input
                      type="file"
                      accept="image/*"
                      multiple
                      disabled={uploadingPickupPhotos}
                      onChange={async (e) => {
                        const files = Array.from(e.target.files || []);
                        e.target.value = '';
                        if (!files.length) return;
                        setUploadingPickupPhotos(true);
                        try {
                          const compressedPhotos = await processFilesWithCompression(files);
                          setPickupPhotos(compressedPhotos);
                          await uploadOrderSimplePhotos(
                            selectedOrderToComplete.id,
                            'pickup',
                            compressedPhotos,
                          );
                          toast.success(t('driver_toast_pickup_photos_uploaded'));
                          setSelectedOrderToComplete((prev) => ({ ...prev, has_pickup_photos: true }));
                          setPickupPhotos([]);
                          loadIncompleteOrders().catch(console.error);
                        } catch (error) {
                          console.error(error);
                          toast.error(error?.message || t('driver_toast_error_upload_pickup_photos'));
                        } finally {
                          setUploadingPickupPhotos(false);
                        }
                      }}
                      className="bg-white"
                    />
                    {uploadingPickupPhotos && (
                      <p className="text-sm text-blue-700 flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {t('driver_uploading')}
                      </p>
                    )}
                    {pickupPhotos.length > 0 && !uploadingPickupPhotos && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {pickupPhotos.map((photo, idx) => (
                          <div key={idx} className="relative">
                            <img src={photo} alt={`Recogida ${idx + 1}`} className="w-16 h-16 object-cover rounded border" />
                          </div>
                        ))}
                        <span className="text-sm text-gray-500 self-center">{t('driver_complete_photo_count', { count: pickupPhotos.length })}</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-green-600">{t('driver_complete_pickup_done')}</p>
                )}
              </div>

              {/* SECCIÓN 2: Fotos de Entrega */}
              <div className="border rounded-lg p-4 bg-green-50">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold flex items-center gap-2 text-green-700">
                    <Image className="w-5 h-5" />
                    {t('driver_complete_section_delivery')}
                  </h3>
                  {selectedOrderToComplete.has_delivery_photos && (
                    <span className="flex items-center gap-1 text-green-600 text-sm font-medium">
                      <CheckCircle className="w-4 h-4" /> {t('driver_complete_status_done')}
                    </span>
                  )}
                </div>
                
                {!selectedOrderToComplete.has_delivery_photos ? (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-600">
                      {t('driver_complete_delivery_hint')}
                    </p>
                    <Input
                      type="file"
                      accept="image/*"
                      multiple
                      disabled={uploadingDeliveryPhotos}
                      onChange={async (e) => {
                        const files = Array.from(e.target.files || []);
                        e.target.value = '';
                        if (!files.length) return;
                        setUploadingDeliveryPhotos(true);
                        try {
                          const compressedPhotos = await processFilesWithCompression(files);
                          setDeliveryPhotos(compressedPhotos);
                          await uploadOrderSimplePhotos(
                            selectedOrderToComplete.id,
                            'delivery',
                            compressedPhotos,
                          );
                          toast.success(t('driver_toast_delivery_photos_uploaded'));
                          setSelectedOrderToComplete((prev) => ({ ...prev, has_delivery_photos: true }));
                          setDeliveryPhotos([]);
                          loadIncompleteOrders().catch(console.error);
                        } catch (error) {
                          console.error(error);
                          toast.error(error?.message || t('driver_toast_error_upload_delivery_photos'));
                        } finally {
                          setUploadingDeliveryPhotos(false);
                        }
                      }}
                      className="bg-white"
                    />
                    {uploadingDeliveryPhotos && (
                      <p className="text-sm text-green-700 flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {t('driver_uploading')}
                      </p>
                    )}
                    {deliveryPhotos.length > 0 && !uploadingDeliveryPhotos && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {deliveryPhotos.map((photo, idx) => (
                          <div key={idx} className="relative">
                            <img src={photo} alt={`Entrega ${idx + 1}`} className="w-16 h-16 object-cover rounded border" />
                          </div>
                        ))}
                        <span className="text-sm text-gray-500 self-center">{t('driver_complete_photo_count', { count: deliveryPhotos.length })}</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-green-600">{t('driver_complete_delivery_done')}</p>
                )}
              </div>

              {/* SECCIÓN 3: Contrato Firmado */}
              <div className="border rounded-lg p-4 bg-purple-50">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold flex items-center gap-2 text-purple-700">
                    <FileText className="w-5 h-5" />
                    {t('driver_complete_section_contract')}
                  </h3>
                  {selectedOrderToComplete.has_contract_photos && (
                    <span className="flex items-center gap-1 text-green-600 text-sm font-medium">
                      <CheckCircle className="w-4 h-4" /> {t('driver_complete_status_done')}
                    </span>
                  )}
                </div>
                
                {!selectedOrderToComplete.has_contract_photos ? (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-600">
                      {t('driver_complete_contract_hint')}
                    </p>
                    <Input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={async (e) => {
                        const files = Array.from(e.target.files);
                        const compressedPhotos = await processFilesWithCompression(files);
                        setContractPhotos(compressedPhotos);
                      }}
                      className="bg-white"
                    />
                    {contractPhotos.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {contractPhotos.map((photo, idx) => (
                          <div key={idx} className="relative">
                            <img src={photo} alt={`Contrato ${idx + 1}`} className="w-16 h-16 object-cover rounded border" />
                            <button
                              onClick={() => setContractPhotos(prev => prev.filter((_, i) => i !== idx))}
                              className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 text-xs flex items-center justify-center"
                            >×</button>
                          </div>
                        ))}
                        <span className="text-sm text-gray-500 self-center">{t('driver_complete_photo_count', { count: contractPhotos.length })}</span>
                      </div>
                    )}
                    <Button
                      onClick={async () => {
                        if (contractPhotos.length === 0) {
                          toast.error(t('driver_toast_select_contract_photo'));
                          return;
                        }
                        setUploadingContractPhotos(true);
                        try {
                          const token = localStorage.getItem('driver_token');
                          const response = await fetch(`${BACKEND_URL}/api/orders/${selectedOrderToComplete.id}/simple-photos`, {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json',
                              'Authorization': `Bearer ${token}`
                            },
                            body: JSON.stringify({
                              photo_type: 'contract',
                              photos: contractPhotos
                            })
                          });
                          if (response.ok) {
                            toast.success(t('driver_toast_contract_uploaded'));
                            // Update local state immediately
                            setSelectedOrderToComplete(prev => ({...prev, has_contract_photos: true}));
                            setContractPhotos([]);
                            // Reload incomplete orders in background (don't wait)
                            loadIncompleteOrders().catch(console.error);
                          } else {
                            const err = await response.json();
                            toast.error(err.detail || t('driver_toast_error_upload_contract'));
                            console.error('Error response:', err);
                          }
                        } catch (error) {
                          toast.error(t('driver_toast_error_upload_contract'));
                        } finally {
                          setUploadingContractPhotos(false);
                        }
                      }}
                      disabled={uploadingContractPhotos || contractPhotos.length === 0}
                      className="w-full bg-purple-600 hover:bg-purple-700"
                    >
                      {uploadingContractPhotos ? t('driver_uploading') : t('driver_upload_contract_photos', { count: contractPhotos.length })}
                    </Button>
                  </div>
                ) : (
                  <p className="text-sm text-green-600">{t('driver_complete_contract_done')}</p>
                )}
              </div>

              {/* SECCIÓN 4: Reporte de Gastos */}
              <div className="border rounded-lg p-4 bg-orange-50">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold flex items-center gap-2 text-orange-700">
                    <DollarSign className="w-5 h-5" />
                    {t('driver_complete_section_expenses')}
                  </h3>
                  {selectedOrderToComplete.has_expense_report && (
                    <span className="flex items-center gap-1 text-green-600 text-sm font-medium">
                      <CheckCircle className="w-4 h-4" /> {t('driver_complete_status_done')}
                    </span>
                  )}
                </div>
                
                {!selectedOrderToComplete.has_expense_report ? (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-600">
                      {t('driver_complete_expense_intro')}
                    </p>
                    
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      <div>
                        <Label className="text-sm">{t('driver_expense_fuel')} (€)</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={expenseForm.fuel_expense}
                          onChange={(e) => setExpenseForm(prev => ({...prev, fuel_expense: parseFloat(e.target.value) || 0}))}
                          className="bg-white"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">{t('driver_expense_tolls')} (€)</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={expenseForm.toll_expense}
                          onChange={(e) => setExpenseForm(prev => ({...prev, toll_expense: parseFloat(e.target.value) || 0}))}
                          className="bg-white"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">{t('driver_expense_taxi')}</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={expenseForm.taxi_expense}
                          onChange={(e) => setExpenseForm(prev => ({...prev, taxi_expense: parseFloat(e.target.value) || 0}))}
                          className="bg-white"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">{t('driver_expense_parking')}</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={expenseForm.parking_expense}
                          onChange={(e) => setExpenseForm(prev => ({...prev, parking_expense: parseFloat(e.target.value) || 0}))}
                          className="bg-white"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">{t('driver_expense_other_label')}</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={expenseForm.other_expense}
                          onChange={(e) => setExpenseForm(prev => ({...prev, other_expense: parseFloat(e.target.value) || 0}))}
                          className="bg-white"
                        />
                      </div>
                      {expenseForm.other_expense > 0 && (
                        <div>
                          <Label className="text-sm">{t('driver_expense_other_description')}</Label>
                          <Input
                            type="text"
                            value={expenseForm.other_expense_description}
                            onChange={(e) => setExpenseForm(prev => ({...prev, other_expense_description: e.target.value}))}
                            placeholder={t('ph_describe_expense')}
                            className="bg-white"
                          />
                        </div>
                      )}
                    </div>
                    
                    {/* Fotos de Facturas */}
                    <div className="border-t pt-3 mt-3">
                      <Label className="text-sm font-medium">{t('driver_complete_receipt_photos_label')}</Label>
                      <Input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={async (e) => {
                          const files = Array.from(e.target.files);
                          const compressedPhotos = await processFilesWithCompression(files, RECEIPT_COMPRESSION);
                          setReceiptPhotos(compressedPhotos);
                          setExpenseForm(prev => ({...prev, receipts: compressedPhotos}));
                        }}
                        className="mt-1 bg-white"
                      />
                      {receiptPhotos.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {receiptPhotos.map((photo, idx) => (
                            <div key={idx} className="relative">
                              <img src={photo} alt={`Factura ${idx + 1}`} className="w-16 h-16 object-cover rounded border" />
                              <button
                                onClick={() => {
                                  const newPhotos = receiptPhotos.filter((_, i) => i !== idx);
                                  setReceiptPhotos(newPhotos);
                                  setExpenseForm(prev => ({...prev, receipts: newPhotos}));
                                }}
                                className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 text-xs flex items-center justify-center"
                              >×</button>
                            </div>
                          ))}
                          <span className="text-sm text-gray-500 self-center">{t('driver_complete_invoice_count', { count: receiptPhotos.length })}</span>
                        </div>
                      )}
                    </div>
                    
                    <Button
                      onClick={async () => {
                        setUploadingExpenses(true);
                        try {
                          const token = localStorage.getItem('driver_token');
                          const response = await fetch(`${BACKEND_URL}/api/drivers/expense-reports`, {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json',
                              'Authorization': `Bearer ${token}`
                            },
                            body: JSON.stringify({
                              order_id: selectedOrderToComplete.id,
                              driver_expense: 0,  // Required field
                              ...expenseForm
                            })
                          });
                          if (response.ok) {
                            toast.success(t('driver_toast_expense_report_sent'));
                            // Update local state immediately
                            setSelectedOrderToComplete(prev => ({...prev, has_expense_report: true}));
                            setExpenseForm({
                              fuel_expense: 0,
                              toll_expense: 0,
                              parking_expense: 0,
                              taxi_expense: 0,
                              other_expense: 0,
                              other_expense_description: '',
                              receipts: []
                            });
                            setReceiptPhotos([]);
                            // Reload incomplete orders in background (don't wait)
                            loadIncompleteOrders().catch(console.error);
                          } else {
                            const err = await response.json();
                            toast.error(err.detail || t('driver_toast_error_send_report'));
                            console.error('Error response:', err);
                            // If error is about existing report, reload data and update UI
                            if (err.detail && err.detail.includes('Ya existe un reporte')) {
                              setSelectedOrderToComplete(prev => ({...prev, has_expense_report: true}));
                              loadIncompleteOrders().catch(console.error);
                            }
                          }
                        } catch (error) {
                          console.error('Network error:', error);
                          toast.error(t('driver_toast_error_connection_expense_report'));
                        } finally {
                          setUploadingExpenses(false);
                        }
                      }}
                      disabled={uploadingExpenses}
                      className="w-full bg-orange-600 hover:bg-orange-700"
                    >
                      {uploadingExpenses ? t('driver_sending') : t('driver_send_expense_report')}
                    </Button>
                  </div>
                ) : (
                  <p className="text-sm text-green-600">{t('driver_complete_expense_done')}</p>
                )}
              </div>

              {/* Resumen de estado */}
              <div className="bg-gray-100 rounded-lg p-4">
                <h4 className="font-medium mb-2">{t('driver_complete_service_status_title')}</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className={`flex items-center gap-2 ${selectedOrderToComplete.has_pickup_photos ? 'text-green-600' : 'text-gray-400'}`}>
                    {selectedOrderToComplete.has_pickup_photos ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                    {t('driver_complete_summary_pickup')}
                  </div>
                  <div className={`flex items-center gap-2 ${selectedOrderToComplete.has_delivery_photos ? 'text-green-600' : 'text-gray-400'}`}>
                    {selectedOrderToComplete.has_delivery_photos ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                    {t('driver_complete_summary_delivery')}
                  </div>
                  <div className={`flex items-center gap-2 ${selectedOrderToComplete.has_contract_photos ? 'text-green-600' : 'text-gray-400'}`}>
                    {selectedOrderToComplete.has_contract_photos ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                    {t('driver_complete_summary_contract')}
                  </div>
                  <div className={`flex items-center gap-2 ${selectedOrderToComplete.has_expense_report ? 'text-green-600' : 'text-gray-400'}`}>
                    {selectedOrderToComplete.has_expense_report ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                    {t('driver_complete_summary_expenses')}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowCompleteServiceDialog(false);
                  setPickupPhotos([]);
                  setDeliveryPhotos([]);
                  setContractPhotos([]);
                  setReceiptPhotos([]);
                  setExpenseForm({
                    fuel_expense: 0,
                    toll_expense: 0,
                    parking_expense: 0,
                    taxi_expense: 0,
                    other_expense: 0,
                    other_expense_description: '',
                    receipts: []
                  });
                }}
              >
                {t('driver_close')}
              </Button>
              {selectedOrderToComplete.has_pickup_photos && selectedOrderToComplete.has_delivery_photos && selectedOrderToComplete.has_expense_report && (
                <Button
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => {
                    toast.success(t('driver_toast_service_completed'));
                    setShowCompleteServiceDialog(false);
                    loadIncompleteOrders();
                  }}
                >
                  {t('driver_complete_finish')}
                </Button>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
      {/* Assign to Fleet Member Dialog */}
      {showAssignDialog && selectedOrderForAssignment && (
        <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {selectedOrderForAssignment.driver_id === driver.id ? t('driver_fleet_assign_service') : t('driver_fleet_reassign_service')}
              </DialogTitle>
              <DialogDescription>
                {t('driver_fleet_assign_desc')}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold mb-2">{t('driver_fleet_service_block_title')}</h4>
                <p className="text-sm text-gray-700">
                  {selectedOrderForAssignment.car_brand} {selectedOrderForAssignment.car_model}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  {selectedOrderForAssignment.pickup_address} → {selectedOrderForAssignment.delivery_address}
                </p>
                {selectedOrderForAssignment.driver_name && (
                  <p className="text-sm text-blue-600 mt-2">
                    {t('driver_fleet_current_assignee')} <strong>{selectedOrderForAssignment.driver_name}</strong>
                  </p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label>{t('driver_select_driver')}</Label>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {/* Fleet driver (himself) option */}
                  <div
                    className={`border rounded-lg p-3 hover:bg-gray-50 cursor-pointer transition-colors ${
                      selectedOrderForAssignment.driver_id === driver.id ? 'border-blue-500 bg-blue-50' : ''
                    }`}
                    onClick={() => handleAssignToFleetMember(driver.id)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-200 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-green-700" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{driver.name} {t('driver_you_short')}</p>
                        <p className="text-xs text-gray-600">{driver.email}</p>
                      </div>
                      {selectedOrderForAssignment.driver_id === driver.id && (
                        <CheckCircle className="w-5 h-5 text-blue-600" />
                      )}
                    </div>
                  </div>
                  
                  {/* Fleet members */}
                  {fleetMembers.filter(m => m.documents_validated).length === 0 ? (
                    <div className="text-center py-6 text-gray-500">
                      <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-yellow-500" />
                      <p className="text-sm">{t('driver_fleet_no_validated_members')}</p>
                      <p className="text-xs mt-1">{t('driver_fleet_no_validated_members_hint')}</p>
                    </div>
                  ) : (
                    fleetMembers
                      .filter(m => m.documents_validated)
                      .map(member => (
                        <div
                          key={member.id}
                          className={`border rounded-lg p-3 hover:bg-gray-50 cursor-pointer transition-colors ${
                            selectedOrderForAssignment.driver_id === member.id ? 'border-blue-500 bg-blue-50' : ''
                          }`}
                          onClick={() => handleAssignToFleetMember(member.id)}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                              <User className="w-5 h-5 text-gray-600" />
                            </div>
                            <div className="flex-1">
                              <p className="font-medium">{member.name}</p>
                              <p className="text-xs text-gray-600">{member.email}</p>
                            </div>
                            {selectedOrderForAssignment.driver_id === member.id ? (
                              <CheckCircle className="w-5 h-5 text-blue-600" />
                            ) : (
                              <CheckCircle className="w-5 h-5 text-gray-300" />
                            )}
                          </div>
                        </div>
                      ))
                  )}
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Incident Report Dialog */}
      <Dialog open={showIncidentDialog} onOpenChange={setShowIncidentDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              {t('driver_report_incident_title')}
            </DialogTitle>
            <DialogDescription>
              {selectedOrderForIncident && (
                <span>
                  {t('driver_fleet_service_block_title')} {selectedOrderForIncident.car_plate} - {selectedOrderForIncident.car_brand} {selectedOrderForIncident.car_model}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Incident Type */}
            <div>
              <Label className="text-sm font-medium">{t('driver_incident_type_label')}</Label>
              <Select
                value={incidentForm.incident_type}
                onValueChange={(value) => setIncidentForm({...incidentForm, incident_type: value})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pickup_unavailable">🚗 {t('admin_incident_type_pickup_unavailable')}</SelectItem>
                  <SelectItem value="vehicle_breakdown">🔧 {t('admin_incident_type_vehicle_breakdown')}</SelectItem>
                  <SelectItem value="accident">💥 {t('admin_incident_type_accident')}</SelectItem>
                  <SelectItem value="minor_damage">⚠️ {t('admin_incident_type_minor_damage')}</SelectItem>
                  <SelectItem value="delay">⏰ {t('admin_incident_type_delay')}</SelectItem>
                  <SelectItem value="other">📝 {t('admin_incident_type_other')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Cancellation Reason - Show for pickup_unavailable */}
            {incidentForm.incident_type === 'pickup_unavailable' && (
              <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                <Label className="text-sm font-medium text-yellow-800">{t('driver_incident_cancel_reason_label')}</Label>
                <textarea
                  className="mt-1 w-full p-2 border rounded-lg text-sm resize-none"
                  rows={3}
                  placeholder={t('driver_incident_cancel_reason_placeholder')}
                  value={incidentForm.cancellation_reason}
                  onChange={(e) => setIncidentForm({...incidentForm, cancellation_reason: e.target.value})}
                />
              </div>
            )}

            {/* General Description */}
            <div>
              <Label className="text-sm font-medium">{t('driver_incident_description_label')}</Label>
              <textarea
                className="mt-1 w-full p-2 border rounded-lg text-sm resize-none"
                rows={3}
                placeholder={t('driver_incident_description_placeholder')}
                value={incidentForm.description}
                onChange={(e) => setIncidentForm({...incidentForm, description: e.target.value})}
              />
            </div>

            {/* Photo Uploads */}
            <div className="space-y-3">
              {/* Contract Photo */}
              <div>
                <Label className="text-sm font-medium flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  {t('driver_incident_contract_photo_label')}
                </Label>
                <div className="mt-1 flex items-center gap-2">
                  <Input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        try {
                          const compressed = await compressImage(file);
                          setIncidentForm({...incidentForm, contract_photo: compressed});
                          toast.success(t('driver_toast_contract_photo_added'));
                        } catch (err) {
                          toast.error(t('driver_toast_image_process_error'));
                        }
                      }
                    }}
                    className="text-sm"
                  />
                  {incidentForm.contract_photo && (
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  )}
                </div>
                {incidentForm.contract_photo && (
                  <img src={incidentForm.contract_photo} alt={t('admin_incident_alt_contract')} className="mt-2 h-20 rounded border" />
                )}
              </div>

              {/* Vehicle Photo */}
              <div>
                <Label className="text-sm font-medium flex items-center gap-2">
                  <Truck className="w-4 h-4" />
                  {t('driver_incident_vehicle_photo_label')}
                </Label>
                <div className="mt-1 flex items-center gap-2">
                  <Input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        try {
                          const compressed = await compressImage(file);
                          setIncidentForm({...incidentForm, vehicle_photo: compressed});
                          toast.success(t('driver_toast_vehicle_photo_added'));
                        } catch (err) {
                          toast.error(t('driver_toast_image_process_error'));
                        }
                      }
                    }}
                    className="text-sm"
                  />
                  {incidentForm.vehicle_photo && (
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  )}
                </div>
                {incidentForm.vehicle_photo && (
                  <img src={incidentForm.vehicle_photo} alt={t('admin_incident_alt_vehicle')} className="mt-2 h-20 rounded border" />
                )}
              </div>
            </div>

            {/* Warning Notice */}
            <div className="bg-red-50 p-3 rounded-lg border border-red-200 text-sm text-red-800">
              <strong>{t('driver_incident_submit_notice_title')}</strong> {t('driver_incident_submit_notice_body')}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 justify-end">
            <Button
              variant="outline"
              onClick={() => {
                setShowIncidentDialog(false);
                setSelectedOrderForIncident(null);
                setIncidentForm({
                  incident_type: 'pickup_unavailable',
                  description: '',
                  cancellation_reason: '',
                  contract_photo: null,
                  vehicle_photo: null
                });
              }}
            >
              {t('cancel')}
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleSubmitIncident}
              disabled={submittingIncident || !incidentForm.description}
            >
              {submittingIncident ? t('driver_sending') : t('driver_report_incident_title')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DriverDashboard;
